import { metersEnabled } from '../audio/playbackProfile';
import { hasVelocityLayers, velocityToneCutoff, VELOCITY_TONE_DEFAULT } from './velocityTone';
import { HUMANIZE_DEFAULT, noteVariation } from '../audio/hitVariation';
import { resampleChannelsHighQuality } from '../audio/samplePreparationClient';
import { PITCH_COPY_STEPS, pitchCopyFor, pitchCopyKey, wantsPitchCopies } from './samplePitchCopies';
import { hostedSampleStore, PRIORITY } from './hostedSampleStore';
import { HOSTED_LIBRARIES, hostedFallbackModel, hostedInstrumentZones, hostedStarterZones } from './hostedLibraries';
import { compileToneTable, compileToneSample, toneTableKey } from './toneAssetCompiler';
import { clamp, normalizeInstrumentPatch } from './patchSchema';
import { loadDspModule } from '../audio/dspModule';
import { CORE_CUSTOM_TABLE, CORE_PARAMS, coreParamBlock, coreVoiceId } from './synthCoreSchema';
import { loadCoreTable, loadCompiledCoreTable, factoryCoreTable } from './coreTableStore';
import { dspAssetUrl } from '../audio/dspAssetUrl';

const MAX_ACTIVE_OSCILLATORS = 256;
// Step-mod automation is scheduled up front per voice. This bounds that
// schedule; noteOff cancels whatever is left at the release, so it only needs
// to outlast any musically plausible held note.
const STEP_MOD_SCHEDULED_STEPS = 256;
const PHYSICAL_WORKLET_URL = dspAssetUrl('/audio/physicalInstrumentProcessor.js');
const physicalWorkletLoads = new WeakMap();
const afmWorkletLoads = new WeakMap();
const coreWorkletLoads = new WeakMap();
const CORE_WORKLET_URL = dspAssetUrl('/audio/synthCoreProcessor.js');
// Notes pressed while the Core engine loads are replayed once it is ready,
// unless they are older than this (a stale note arriving late is worse).
const CORE_PENDING_MAX_AGE = 0.25;
const crossfadedLoopBuffers = new WeakMap();
const AUDIO_DIAGNOSTICS_ENABLED = (
  (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production')
  || globalThis.__COOKUP_AUDIO_DIAGNOSTICS__ === true
);

// One compiled module shared with every other CookUp worklet.
const loadPhysicalDspModule = loadDspModule;

const PHYSICAL_PROFILES = Object.freeze({
  piano: { ratios: [1, 2.01, 3.03, 4.08, 5.16, 6.28, 8.55, 10.8], gains: [1, .62, .34, .22, .15, .1, .055, .035], burst: .026, decay: 1, sustain: false, anchor: true },
  pluck: { ratios: [1, 2, 3, 4, 5, 6, 7, 8], gains: [1, .72, .48, .31, .22, .16, .11, .08], burst: .014, decay: .56, sustain: false, anchor: true },
  mallet: { ratios: [1, 2.76, 5.4, 8.93, 13.3, 18.6], gains: [1, .55, .32, .19, .11, .07], burst: .035, decay: .72, sustain: false, anchor: false },
  bell: { ratios: [1, 2.01, 2.4, 3, 4.15, 5.4, 6.8, 8.9], gains: [1, .66, .53, .38, .28, .2, .13, .08], burst: .018, decay: 1.45, sustain: false, anchor: false },
  bowed: { ratios: [1, 2, 3, 4, 5, 6, 7], gains: [1, .58, .37, .24, .16, .1, .065], burst: .12, decay: 1.15, sustain: true, anchor: true },
  wind: { ratios: [1, 2, 3, 4, 5], gains: [1, .38, .2, .11, .06], burst: .11, decay: .8, sustain: true, anchor: true },
  reed: { ratios: [1, 2, 3, 4, 5, 6], gains: [1, .72, .39, .26, .14, .09], burst: .08, decay: .9, sustain: true, anchor: true },
  brass: { ratios: [1, 2, 3, 4, 5, 6, 7, 8], gains: [1, .48, .7, .32, .46, .22, .27, .14], burst: .07, decay: .9, sustain: true, anchor: true }
});

export const midiToFrequency = (midiNote) => 440 * Math.pow(2, (Number(midiNote) - 69) / 12);

export const distortionCurve = (amount = 0, mix = 0) => {
  const samples = 2048;
  const curve = new Float32Array(samples);
  const drive = 1 + clamp(amount, 0, 1, 0) * 12;
  const wet = clamp(mix, 0, 1, 0);
  for (let index = 0; index < samples; index += 1) {
    const x = (index * 2) / (samples - 1) - 1;
    const shaped = Math.tanh(x * drive) / Math.tanh(drive);
    curve[index] = (x * (1 - wet)) + (shaped * wet);
  }
  return curve;
};

const createImpulse = (ctx, size = 0.4, damping = 0.5) => {
  const duration = 0.35 + clamp(size, 0, 1, 0.4) * 4.5;
  const length = Math.max(1, Math.floor(ctx.sampleRate * duration));
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let index = 0; index < length; index += 1) {
      const progress = index / length;
      const envelope = Math.pow(1 - progress, 1.2 + damping * 3.8);
      data[index] = (Math.random() * 2 - 1) * envelope;
    }
  }
  return buffer;
};

const createNoiseBuffer = (ctx, color = 'pink') => {
  const length = Math.max(1, Math.floor(ctx.sampleRate * 2));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let previous = 0;
  for (let index = 0; index < length; index += 1) {
    const white = Math.random() * 2 - 1;
    if (color === 'white') data[index] = white * 0.55;
    else if (color === 'brown') {
      previous = clamp(previous * 0.985 + white * 0.015, -1, 1, 0);
      data[index] = previous * 2.4;
    } else {
      previous = previous * 0.82 + white * 0.18;
      data[index] = previous * 1.8;
    }
  }
  return buffer;
};

export const createCrossfadedLoopBuffer = (ctx, original, loopStart, loopEnd, crossfade) => {
  if (!original || !ctx?.createBuffer) return original;
  const sampleRate = Number(original.sampleRate) || Number(ctx.sampleRate) || 44100;
  const duration = Number(original.duration) || original.length / sampleRate;
  const start = clamp(loopStart, 0, Math.max(0, duration - 0.002), 0);
  const end = clamp(loopEnd, start + 0.002, duration, duration);
  const fade = Math.min(clamp(crossfade, 0, 2, 0), (end - start) * 0.45);
  if (fade <= 0.0005) return original;
  const signature = `${start.toFixed(5)}:${end.toFixed(5)}:${fade.toFixed(5)}`;
  let variants = crossfadedLoopBuffers.get(original);
  if (!variants) {
    variants = new Map();
    crossfadedLoopBuffers.set(original, variants);
  }
  if (variants.has(signature)) return variants.get(signature);
  const channels = Math.max(1, Number(original.numberOfChannels) || 1);
  const copy = ctx.createBuffer(channels, original.length, sampleRate);
  const startFrame = Math.floor(start * sampleRate);
  const endFrame = Math.min(original.length, Math.floor(end * sampleRate));
  const fadeFrames = Math.max(1, Math.min(endFrame - startFrame - 1, Math.floor(fade * sampleRate)));
  for (let channel = 0; channel < channels; channel += 1) {
    const sourceData = original.getChannelData(channel);
    const targetData = copy.getChannelData(channel);
    targetData.set(sourceData);
    for (let frame = 0; frame < fadeFrames; frame += 1) {
      const progress = frame / Math.max(1, fadeFrames - 1);
      const tailIndex = endFrame - fadeFrames + frame;
      const headIndex = Math.min(original.length - 1, startFrame + frame);
      const dry = sourceData[tailIndex];
      const incoming = sourceData[headIndex];
      const dryGain = Math.cos(progress * Math.PI * 0.5);
      const incomingGain = Math.sin(progress * Math.PI * 0.5);
      targetData[tailIndex] = dry * dryGain + incoming * incomingGain;
    }
  }
  variants.set(signature, copy);
  return copy;
};

const disconnectNode = (node) => {
  try { node?.disconnect(); } catch (_) { /* already disconnected */ }
};

export class NativeInstrumentEngine {
  constructor(audioContext, destination, initialPatch) {
    if (!audioContext) throw new Error('An AudioContext is required');
    if (destination?.context && destination.context !== audioContext) {
      throw new Error('Instrument destination belongs to a different AudioContext');
    }
    this.ctx = audioContext;
    this.destination = destination || audioContext.destination;
    this.patch = normalizeInstrumentPatch(initialPatch);
    this.voices = new Map();
    this.voiceOrder = [];
    this.pitchBend = 0;
    this.lastPlayedNote = null;
    this.tempo = 120;
    this.destroyed = false;
    this.afmModule = null;
    this.toneLayerAssets = new Map();
    this.toneUploads = new Map();
    this.toneJobs = new Map();
    this.toneOfflineAssets = new Map();
    this.wavetableWaves = new Map();
    this.wavetableWaveCache = new Map();
    this.afmPool = [];
    this.afmReady = null;
    this.physicalWorklet = null;
    this.physicalWorkletError = null;
    this.physicalDspModule = null;
    this.physicalDspBackend = 'pending';
    this.physicalDspDiagnostics = null;
    // CookUp Core: one Rust synth worklet per Core layer (see _syncCoreLayers).
    this.coreLayers = new Map();
    this.coreModule = null;
    this.coreInit = null;
    this.coreUnavailable = false;
    this.sustainPedal = false;
    this.sustainedVoiceIds = new Set();
    this.modWheel = 0;
    this.reverbSignature = '';
    this.sampleBuffers = new Map();
    this.pitchCopies = new Map(); // band-limited shifted copies of single recordings (samplePitchCopies.js)
    this.pitchCopyJobs = new Map();
    this.sampleLoads = new Map();
    this.sampleLoadErrors = new Map();
    this.sampleRoundRobin = new Map();
    this.sampleLoadPromise = Promise.resolve([]);
    // CookUp-hosted libraries: zones per layer from the manifest, samples streamed per note.
    this.hostedStore = hostedSampleStore;
    this.hostedLayers = new Map();
    this.hostedPinned = new Map(); // offline renders keep what their notes need, whatever the store evicts
    this.notePreparePromise = Promise.resolve([]);
    this.noiseBuffers = {
      white: createNoiseBuffer(audioContext, 'white'),
      pink: createNoiseBuffer(audioContext, 'pink'),
      brown: createNoiseBuffer(audioContext, 'brown')
    };
    this._buildGraph();
    this.updatePatch(this.patch);
    // Offline renders collect physical-model events instead of posting them.
    // port.postMessage reaches the audio thread asynchronously and
    // startRendering() can finish the whole mix before the queue is drained, so
    // the schedule is handed to the processor through processorOptions in
    // finalizeOfflineRender() instead — identical DSP to live playback.
    this._offlineWorkletEvents = null;
    this.physicalWorkletPromise = this.isOfflineContext()
      ? this._initOfflinePhysicalWorklet()
      : this._initPhysicalWorklet();
  }

  /** True for an OfflineAudioContext (mixdown/export) rather than live output. */
  isOfflineContext() {
    return typeof this.ctx?.startRendering === 'function';
  }

  /** True while physical-model notes should be routed to the worklet DSP. */
  _usesPhysicalWorklet() {
    return Boolean(this.physicalWorklet || this._offlineWorkletEvents);
  }

  /** Post a worklet event live, or queue it for an offline render. */
  _sendPhysicalEvent(message) {
    if (this._offlineWorkletEvents) {
      this._offlineWorkletEvents.push(message);
      return true;
    }
    if (this.physicalWorklet) {
      this.physicalWorklet.port.postMessage(message);
      return true;
    }
    return false;
  }

  _observePhysicalBackend(node) {
    if (!node?.port) return;
    node.port.onmessage = ({ data } = {}) => {
      if (data?.type === 'dspDiagnostics') {
        this.physicalDspDiagnostics = {
          ...data,
          receivedAt: Date.now()
        };
        return;
      }
      if (data?.type === 'dspBackend') {
        this.physicalDspBackend = String(data.backend || 'unavailable');
        this.physicalDspBackendVersion = Number(data.version) || 0;
        this.physicalDspRecovered = data.recovered === true;
        if (data.backend === 'unavailable') this._abandonPhysicalWorklet(node);
      }
    };
    node.onprocessorerror = (error) => {
      this.physicalWorkletError = error;
      this.physicalDspBackend = 'processor-error';
      this._abandonPhysicalWorklet(node);
    };
  }

  /**
   * The worklet cannot render (no usable WASM, or the engine failed and could
   * not be rebuilt). Stop routing notes to it so later notes use the modal
   * synthesis fallback instead of a silent node.
   */
  _abandonPhysicalWorklet(node) {
    if (this.physicalWorklet !== node) return;
    this.physicalWorklet = null;
    try { node.disconnect(); } catch (_) { /* already disconnected */ }
  }

  /**
   * Offline: load the module up front so the engine knows before any note is
   * scheduled whether the worklet path is available. Nothing is routed to the
   * modal fallback unless it genuinely cannot be used, which keeps an export
   * sonically identical to live playback.
   */
  async _initOfflinePhysicalWorklet() {
    if (!this.ctx?.audioWorklet || typeof globalThis.AudioWorkletNode !== 'function') return false;
    try {
      let load = physicalWorkletLoads.get(this.ctx);
      if (!load) {
        load = this.ctx.audioWorklet.addModule(PHYSICAL_WORKLET_URL);
        physicalWorkletLoads.set(this.ctx, load);
      }
      const [, dspModule] = await Promise.all([load, loadPhysicalDspModule()]);
      if (this.destroyed) return false;
      // The worklet has no JavaScript DSP: without WASM, modal synthesis renders.
      if (!dspModule) throw new Error('CookUp DSP WebAssembly module unavailable');
      this.physicalDspModule = dspModule;
      this._offlineWorkletEvents = [];
      this.physicalWorkletError = null;
      return true;
    } catch (error) {
      // Modal synthesis still renders; only the timbre differs.
      this.physicalWorkletError = error;
      physicalWorkletLoads.delete(this.ctx);
      this._offlineWorkletEvents = null;
      return false;
    }
  }

  /**
   * Build the worklet node for an offline render once the whole song has been
   * scheduled, passing the collected events through processorOptions. Must be
   * called after the last noteOn/noteOff and before startRendering().
   */
  async finalizeOfflineRender() {
    await this._finalizeOfflineCore();
    if (!this._offlineWorkletEvents) return false;
    const events = this._offlineWorkletEvents;
    this._offlineWorkletEvents = null;
    if (events.length === 0) return true;
    try {
      await this.physicalWorkletPromise;
      const nodeOptions = {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
        channelCount: 2,
        channelCountMode: 'explicit',
        processorOptions: { events, dspModule: this.physicalDspModule, diagnostics: AUDIO_DIAGNOSTICS_ENABLED }
      };
      const node = new globalThis.AudioWorkletNode(this.ctx, 'cookup-physical-instrument', nodeOptions);
      this._observePhysicalBackend(node);
      node.connect(this.input);
      this.physicalWorklet = node;
      return true;
    } catch (error) {
      this.physicalWorkletError = error;
      console.error('Physical instrument worklet unavailable for export', error);
      return false;
    }
  }

  async _initPhysicalWorklet() {
    if (!this.ctx?.audioWorklet || typeof globalThis.AudioWorkletNode !== 'function') return false;
    let load = physicalWorkletLoads.get(this.ctx);
    if (!load) {
      load = this.ctx.audioWorklet.addModule(PHYSICAL_WORKLET_URL);
      physicalWorkletLoads.set(this.ctx, load);
    }
    try {
      const [, dspModule] = await Promise.all([load, loadPhysicalDspModule()]);
      if (this.destroyed) return false;
      // The worklet has no JavaScript DSP: without WASM, modal synthesis renders.
      if (!dspModule) throw new Error('CookUp DSP WebAssembly module unavailable');
      this.physicalDspModule = dspModule;
      const nodeOptions = {
        numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2],
        channelCount: 2, channelCountMode: 'explicit',
        processorOptions: { dspModule, diagnostics: AUDIO_DIAGNOSTICS_ENABLED }
      };
      // Engines that cannot structured-clone a WebAssembly.Module throw here
      // and land on the modal synthesis fallback below.
      const node = new globalThis.AudioWorkletNode(this.ctx, 'cookup-physical-instrument', nodeOptions);
      this._observePhysicalBackend(node);
      node.connect(this.input);
      this.physicalWorklet = node;
      this.physicalWorkletError = null;
      return true;
    } catch (error) {
      // WebAudio oscillator/modal synthesis remains available on browsers or
      // offline contexts that cannot load an AudioWorklet module.
      this.physicalWorkletError = error;
      physicalWorkletLoads.delete(this.ctx);
      return false;
    }
  }

  ready() {
    return Promise.all([
      this.physicalWorkletPromise || Promise.resolve(false),
      this.afmReady || Promise.resolve(false),
      (async () => {
        if (this.coreLayers.size === 0) return true;
        const available = await this.coreInit;
        await Promise.all([...this.coreLayers.values()].flatMap(entry => entry.tableLoads));
        if (!available || this.coreUnavailable || [...this.coreLayers.values()].some(entry => entry.failed)) {
          throw this.coreError || new Error('The selected synth could not load. Please retry.');
        }
        return true;
      })(),
      this.sampleLoadPromise || Promise.resolve([]),
      this.notePreparePromise || Promise.resolve([]),
      Promise.all([...this.toneJobs.values()]),
      // Exports wait for the clean pitch copies; live notes use the original until they arrive.
      Promise.all([...this.pitchCopyJobs.values()])
    ]).then(([physicalReady]) => {
      for (const layer of this.patch.layers) {
        const entry = this.hostedLayers.get(layer.id);
        if (layer.enabled && layer.level > 0.0001 && entry?.strict && entry.unavailable) throw entry.error;
      }
      return physicalReady;
    });
  }

  registerSampleBuffer(bufferId, audioBuffer) {
    if (!bufferId || !audioBuffer?.getChannelData) return false;
    this.sampleBuffers.set(String(bufferId), audioBuffer);
    this.sampleLoadErrors.delete(String(bufferId));
    this._preparePitchCopies(String(bufferId), audioBuffer);
    return true;
  }

  unregisterSampleBuffer(bufferId) {
    PITCH_COPY_STEPS.forEach((semitones) => this.pitchCopies.delete(pitchCopyKey(String(bufferId), semitones)));
    return this.sampleBuffers.delete(String(bufferId));
  }

  /** Prepare band-limited copies 6–24 semitones up, off the audio and UI threads. Never fails a note. */
  _preparePitchCopies(key, buffer) {
    if (!wantsPitchCopies(buffer) || typeof this.ctx.createBuffer !== 'function') return;
    const channels = Array.from({ length: buffer.numberOfChannels || 1 }, (_, channel) => buffer.getChannelData(channel));
    const job = Promise.all(PITCH_COPY_STEPS.map(async (semitones) => {
      const prepared = await resampleChannelsHighQuality(channels, 2 ** (semitones / 12), { taps: 32 });
      if (this.sampleBuffers.get(key) !== buffer || !prepared?.channels?.[0]?.length) return;
      const copy = this.ctx.createBuffer(prepared.channels.length, prepared.channels[0].length, buffer.sampleRate);
      prepared.channels.forEach((data, channel) => copy.copyToChannel(data, channel));
      this.pitchCopies.set(pitchCopyKey(key, semitones), copy);
    })).catch(() => null).finally(() => {
      if (this.pitchCopyJobs.get(key) === job) this.pitchCopyJobs.delete(key);
    });
    this.pitchCopyJobs.set(key, job);
  }

  prepareSamples(candidate = this.patch) {
    const patch = normalizeInstrumentPatch(candidate);
    const zones = patch.layers.flatMap((layer) => ['sample','granular'].includes(layer.engine) ? layer.sampleZones : []);
    const pending = zones.map(async (zone) => {
      const key = zone.bufferId || zone.url;
      if (!key || this.sampleBuffers.has(key) || !zone.url || typeof fetch !== 'function') return null;
      if (this.sampleLoads.has(key)) return this.sampleLoads.get(key);
      const load = (async () => { try {
        const response = await fetch(zone.url);
        if (!response.ok) throw new Error(`Sample request failed (${response.status})`);
        const buffer = await this.ctx.decodeAudioData(await response.arrayBuffer());
        this.registerSampleBuffer(key, buffer);
        return key;
      } catch (error) {
        this.sampleLoadErrors.set(key, error);
        return null;
      } finally {
        this.sampleLoads.delete(key);
      } })();
      this.sampleLoads.set(key, load);
      return load;
    });
    const hosted = this._prepareHostedLayers(patch);
    this.sampleLoadPromise = Promise.all([...pending, hosted]);
    return this.sampleLoadPromise;
  }

  /** Load each hosted layer's manifest and, per key range, one recording so every key sounds at once. */
  _prepareHostedLayers(patch) {
    const wanted = new Map(patch.layers
      .filter((layer) => layer.engine === 'sample' && layer.hostedInstrument)
      .map((layer) => [layer.id, layer.hostedInstrument]));
    [...this.hostedLayers.keys()].forEach((layerId) => {
      const target = wanted.get(layerId);
      const entry = this.hostedLayers.get(layerId);
      if (!target || entry.key !== `${target.library}/${target.instrument}`) this.hostedLayers.delete(layerId);
    });
    return Promise.all([...wanted].map(([layerId, { library, instrument }]) => {
      const existing = this.hostedLayers.get(layerId);
      if (existing) return existing.ready;
      const entry = { strict: HOSTED_LIBRARIES[library]?.renderedSynth === true, key: `${library}/${instrument}`, manifest: null, zones: [], error: null, ready: null };
      this.hostedLayers.set(layerId, entry);
      entry.ready = (async () => {
        try {
          const manifest = await this.hostedStore.manifest(library);
          entry.manifest = manifest;
          entry.zones = hostedInstrumentZones(manifest, instrument);
          if (!entry.zones.length) throw new Error(`${library} has no instrument ${instrument}.`);
          // Offline mixdowns load only the exact FLAC recordings in prepareNotes.
          if (this.isOfflineContext()) {
            if (!manifest.tiers?.lossless) throw new Error(`${library} has no lossless recordings for export.`);
            return entry.key;
          }
          // Short previews (a few seconds, low bitrate) when the library has them: every key
          // sounds within a moment of opening; full recordings stream in as keys are played.
          const tier = !HOSTED_LIBRARIES[library]?.fullStarter && manifest.tiers?.preview ? 'preview' : null;
          const starters = await Promise.all(hostedStarterZones(entry.zones)
            .map(async (zone) => (await this.hostedStore.request(this.ctx, manifest, zone.sampleId, PRIORITY.starter, tier))
              // No preview to be had: start from the full recording instead.
              || (tier ? this.hostedStore.request(this.ctx, manifest, zone.sampleId, PRIORITY.starter) : null)));
          // Recorded synths require every starter; older acoustic libraries retain partial fallback.
          if (entry.strict ? starters.some(buffer => !buffer) : !starters.some(Boolean)) throw new Error(`${library} recordings are unavailable.`);
          return entry.key;
        } catch (error) {
          entry.unavailable = true;
          entry.error = error;
          this.sampleLoadErrors.set(entry.key, error);
          return null;
        }
      })();
      return entry.ready;
    }));
  }

  _pinHosted(manifest, sampleId, buffer) {
    // Offline renders schedule a whole song at once, so they hold on to every sample they asked for.
    if (buffer && typeof this.ctx.startRendering === 'function') this.hostedPinned.set(`${manifest.id}:${sampleId}`, buffer);
    return buffer;
  }

  /**
   * Stream the recordings these notes will play ({ note, velocity } pairs): every round
   * robin and release sample of each, for exports and for the arrangement about to play.
   * Live keyboard notes need no call: an unloaded note plays its nearest loaded
   * recording and requests its own.
   */
  prepareNotes(notes = []) {
    const list = Array.from(notes || []).filter((entry) => Number.isFinite(Number(entry?.note)));
    const jobs = this.patch.layers.map((layer) => {
      const entry = this.hostedLayers.get(layer.id);
      if (!entry || !list.length) return null;
      return entry.ready.then(() => {
        if (!entry.manifest || entry.unavailable) {
          if (this.isOfflineContext()) throw new Error(`Cannot export hosted instrument: ${entry.error?.message || 'library unavailable'}`);
          return [];
        }
        const wanted = new Set();
        list.forEach(({ note, velocity = 0.8 }) => {
          const midi = Math.round(Number(note));
          const level = clamp(velocity, 0.01, 1, 0.8);
          entry.zones.forEach((zone) => {
            if (zone.trigger.startsWith('pedal')) return; // live MIDI only; arrangements carry no pedal
            if (midi >= zone.loNote && midi <= zone.hiNote && level >= zone.loVelocity && level <= zone.hiVelocity) wanted.add(zone.sampleId);
          });
        });
        return Promise.all([...wanted].map((sampleId) => this.hostedStore
          .request(this.ctx, entry.manifest, sampleId, PRIORITY.upcoming, this.isOfflineContext() ? 'lossless' : null)
          .then((buffer) => {
            if (!buffer && this.isOfflineContext()) throw new Error(`Lossless recording ${sampleId} could not load; export stopped. Retry when the library is available.`);
            return this._pinHosted(entry.manifest, sampleId, buffer);
          })));
      });
    }).filter(Boolean);
    if (!jobs.length) return Promise.resolve([]);
    const prepared = Promise.all(jobs);
    this.notePreparePromise = Promise.all([this.notePreparePromise, prepared]).then(() => []);
    // Retain the failure for ready(), with a handler while the caller awaits prepared.
    this.notePreparePromise.catch(() => {});
    return prepared;
  }

  /** A layer's zones: from its hosted library when it has one, else its own. */
  _layerZones(layer) {
    const hosted = this.hostedLayers.get(layer.id);
    return hosted?.zones?.length ? hosted.zones : (layer.sampleZones || []);
  }

  _zoneBuffer(layer, zone, tier = null) {
    if (zone.sampleId) {
      const manifestId = this.hostedLayers.get(layer.id)?.manifest?.id;
      if (!manifestId) return null;
      if (tier === 'preview') return this.hostedStore.get(manifestId, zone.sampleId, 'preview');
      return this.hostedPinned.get(`${manifestId}:${zone.sampleId}`) || this.hostedStore.get(manifestId, zone.sampleId, this.isOfflineContext() ? 'lossless' : null);
    }
    return this.sampleBuffers.get(zone.bufferId || zone.url) || null;
  }

  /** The loaded recording nearest the one wanted: least re-pitching first, then the closest velocity. */
  _hostedFallback(layer, zones, wanted, note, velocity, trigger, tier = null) {
    let best = null;
    zones.forEach((zone) => {
      if (zone.trigger !== trigger || !this._zoneBuffer(layer, zone, tier)) return;
      const keyDistance = note < zone.loNote ? zone.loNote - note : note > zone.hiNote ? note - zone.hiNote : 0;
      const velocityDistance = velocity < zone.loVelocity ? zone.loVelocity - velocity
        : velocity > zone.hiVelocity ? velocity - zone.hiVelocity : 0;
      const shift = Math.abs(note - zone.rootNote); // semitones the recording would be re-pitched
      if (keyDistance > 6 || shift > 7) return;
      const score = shift + velocityDistance * 6 + (zone.rootNote === wanted.rootNote ? 0 : 0.01);
      if (!best || score < best.score) best = { zone, score };
    });
    return best?.zone || null;
  }

  _buildGraph() {
    const ctx = this.ctx;
    this.input = ctx.createGain();
    this.shaper = ctx.createWaveShaper();
    this.shaper.oversample = '4x';
    this.dryGain = ctx.createGain();
    this.chorusDelay = ctx.createDelay(0.08);
    this.chorusWet = ctx.createGain();
    this.chorusLfo = ctx.createOscillator();
    this.chorusDepth = ctx.createGain();
    this.delay = ctx.createDelay(2);
    this.delayFeedback = ctx.createGain();
    this.delayWet = ctx.createGain();
    this.convolver = ctx.createConvolver();
    this.reverbWet = ctx.createGain();
    this.compressor = ctx.createDynamicsCompressor();
    this.master = ctx.createGain();
    this.safetyLimiter = ctx.createDynamicsCompressor();
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    this.analyser.smoothingTimeConstant = 0.72;

    this.input.connect(this.shaper);
    this.shaper.connect(this.dryGain);
    this.dryGain.connect(this.compressor);

    // The three sends below are connected by _routeSend only while their
    // effect is audible. Turning a wet gain to zero does not stop a delay line
    // or a convolution from running; taking away its input does.
    this.sends = {};
    this.chorusDelay.connect(this.chorusWet);
    this.chorusWet.connect(this.compressor);
    this.chorusLfo.connect(this.chorusDepth);
    this.chorusDepth.connect(this.chorusDelay.delayTime);
    this.chorusLfo.start();

    this.delay.connect(this.delayFeedback);
    this.delayFeedback.connect(this.delay);
    this.delay.connect(this.delayWet);
    this.delayWet.connect(this.compressor);

    this.convolver.connect(this.reverbWet);
    this.reverbWet.connect(this.compressor);

    this.compressor.connect(this.master);
    this.safetyLimiter.threshold.value = -3;
    this.safetyLimiter.knee.value = 0;
    this.safetyLimiter.ratio.value = 20;
    this.safetyLimiter.attack.value = 0.001;
    this.safetyLimiter.release.value = 0.08;
    this.master.connect(this.safetyLimiter);
    // Remember the actual output branch. Session refresh replaces channel
    // inputs, and reconnecting the unused analyser in chat would leave the
    // audible limiter attached to the old, disconnected channel.
    this.outputNode = metersEnabled() ? this.analyser : this.safetyLimiter;
    if (this.outputNode === this.analyser) {
      this.safetyLimiter.connect(this.analyser);
    }
    this.outputNode.connect(this.destination);
  }

  /** Feed an effect only while it can be heard; an unfed effect costs nothing once its tail has rung out. */
  _routeSend(name, node, audible) {
    if (Boolean(this.sends[name]) === Boolean(audible)) return;
    try {
      if (audible) this.shaper.connect(node);
      else this.shaper.disconnect(node);
    } catch (_) { /* Already in the requested state. */ }
    this.sends[name] = Boolean(audible);
  }

  _effectiveVoiceLimit(patch = this.patch) {
    const audibleLayers = patch.layers.filter((layer) => layer.enabled && layer.level > 0.0001);
    // Core layers manage their own polyphony (and voice stealing) in Rust.
    // When nothing else is playing, don't let the JS bookkeeping cut notes.
    if (audibleLayers.length > 0 && audibleLayers.every((layer) => layer.engine === 'core')) return 64;
    const layerCost = patch.layers
      .filter((layer) => layer.enabled && layer.level > 0.0001 && layer.engine !== 'core')
      .reduce((total, layer) => {
        if (layer.engine === 'granular') return total + 16;
        if (layer.engine === 'spectral') return total + layer.spectral.partials;
        if (layer.engine === 'analog' || layer.engine === 'wavetable') return total + Math.max(1, layer.unison || 1);
        if (layer.engine === 'afm') return total + 6;
        if (layer.engine === 'fm') return total + 2;
        if (layer.engine === 'additive' || layer.engine === 'resonator') {
          return total + Math.max(1, layer.harmonics.filter((value) => value > 0.0001).length);
        }
        if (layer.engine === 'physical') return total + 9;
        return total + 1;
      }, 0);
    const modulationCost = [patch.lfo, patch.lfo2].filter((lfo) => lfo?.enabled && lfo.depth > 0.0001).length;
    // Complexity may reduce the requested polyphony, but never collapse an
    // instrument into monophonic behavior. Even the densest patch keeps two
    // independent voices so intervals and chords remain playable.
    const safeLimit = Math.max(2, Math.floor(MAX_ACTIVE_OSCILLATORS / Math.max(1, layerCost + modulationCost)));
    return Math.min(patch.performance.voices, safeLimit);
  }

  connect(destination) {
    if (!destination || destination === this.destination) return;
    disconnectNode(this.outputNode);
    this.destination = destination;
    this.outputNode.connect(destination);
  }

  updatePatch(candidate) {
    this.patch = normalizeInstrumentPatch(candidate);
    if (!this.afmReady && this.patch.layers.some(layer => layer.enabled && ['afm','wavetable','spectral','granular'].includes(layer.engine))) this.afmReady = this._initAfm();
    this._prepareWavetables();
    this.prepareSamples(this.patch);
    this._prepareToneAssets();
    this._syncCoreLayers();
    const now = this.ctx.currentTime;
    this.voices.forEach(voice=>voice.toneNodes?.forEach(({node,layerId})=>{
      const layer=this.patch.layers.find(entry=>entry.id===layerId);
      if(!layer)return;
      Object.entries(this._toneValues(layer)).forEach(([name,value])=>node.parameters.get(name).setTargetAtTime(value,now,.02));
    }));
    const { effects, performance, macros } = this.patch;
    const dirt = clamp(
      effects.distortion.drive * 0.65 + macros.Dirt * 0.25 + this.patch.filter.drive * 0.1,
      0,
      1,
      0
    );
    // With no distortion mixed in the curve is a straight line, so skip the
    // shaper and its 4x oversampling filters instead of running them for nothing.
    const distortionMix = effects.distortion.enabled ? clamp(effects.distortion.mix, 0, 1, 0) : 0;
    this.shaper.curve = distortionMix > 0 ? distortionCurve(dirt, distortionMix) : null;
    this.shaper.oversample = distortionMix > 0 ? '4x' : 'none';
    this.dryGain.gain.setTargetAtTime(1, now, 0.01);

    const chorusMix = effects.chorus.enabled ? clamp(effects.chorus.mix + macros.Width * 0.18, 0, 0.8, 0) : 0;
    const motion = clamp(macros.Motion, 0, 1, 0);
    this._routeSend('chorus', this.chorusDelay, chorusMix > 0);
    this.chorusWet.gain.setTargetAtTime(chorusMix, now, 0.02);
    this.chorusDelay.delayTime.setTargetAtTime(0.012 + effects.chorus.depth * 0.012, now, 0.02);
    this.chorusLfo.frequency.setTargetAtTime(0.05 + effects.chorus.rate * 3 + motion * 1.8, now, 0.02);
    this.chorusDepth.gain.setTargetAtTime(0.0005 + effects.chorus.depth * 0.004 + motion * 0.0025, now, 0.02);

    const delayMix = effects.delay.enabled ? clamp(effects.delay.mix, 0, 0.75, 0.1) : 0;
    this._routeSend('delay', this.delay, delayMix > 0);
    this.delay.delayTime.setTargetAtTime(clamp(effects.delay.time, 0.02, 1.5, 0.25), now, 0.02);
    this.delayFeedback.gain.setTargetAtTime(
      effects.delay.enabled ? clamp(effects.delay.feedback, 0, 0.88, 0.25) : 0,
      now,
      0.02
    );
    this.delayWet.gain.setTargetAtTime(delayMix, now, 0.02);

    const reverbMix = effects.reverb.enabled ? clamp(effects.reverb.mix + macros.Space * 0.22, 0, 0.75, 0.1) : 0;
    const reverbSignature = `${Number(effects.reverb.size).toFixed(3)}:${Number(effects.reverb.damping).toFixed(3)}`;
    // The impulse is seconds of generated noise; only build it for a reverb that is on.
    if (reverbMix > 0 && reverbSignature !== this.reverbSignature) {
      this.convolver.buffer = createImpulse(this.ctx, effects.reverb.size, effects.reverb.damping);
      this.reverbSignature = reverbSignature;
    }
    this._routeSend('reverb', this.convolver, reverbMix > 0);
    this.reverbWet.gain.setTargetAtTime(reverbMix, now, 0.02);

    const compressor = effects.compressor;
    this.compressor.threshold.setTargetAtTime(compressor.enabled ? compressor.threshold : 0, now, 0.02);
    this.compressor.ratio.setTargetAtTime(compressor.enabled ? compressor.ratio : 1, now, 0.02);
    this.compressor.attack.setTargetAtTime(compressor.attack, now, 0.02);
    this.compressor.release.setTargetAtTime(compressor.release, now, 0.02);
    this.master.gain.setTargetAtTime(clamp(performance.master, 0, 1, 0.7) * 0.72, now, 0.01);

    this.voices.forEach((voice) => {
      if (voice.filter) voice.filter.type = this.patch.filter.enabled ? this.patch.filter.type : 'allpass';
      if (voice.filter2) {
        voice.filter2.type = this.patch.filter2.type;
        voice.filter2.frequency.setTargetAtTime(this.patch.filter2.cutoff, now, 0.025);
        voice.filter2.Q.setTargetAtTime(this.patch.filter2.resonance, now, 0.025);
      }
      this._updateVoiceTone(voice, false);
    });
    const voiceLimit = this._effectiveVoiceLimit(this.patch);
    while (this.voiceOrder.length > voiceLimit) {
      const stolenVoiceId = this.voiceOrder.shift();
      this.noteOff(stolenVoiceId, true);
    }
    return this.patch;
  }

  _layerFrequency(note, layer) {
    const transpose = layer.octave * 12 + layer.semitone;
    return midiToFrequency(note + transpose);
  }

  _setPitchedFrequency(param, target, layer, now, multiplier = 1) {
    const glide = clamp(this.patch.performance.glide, 0, 2, 0);
    if (this.lastPlayedNote == null || glide <= 0.0001) {
      param.setValueAtTime(target, now);
      return;
    }
    const previous = Math.max(0.001, this._layerFrequency(this.lastPlayedNote, layer) * multiplier);
    param.setValueAtTime(previous, now);
    param.exponentialRampToValueAtTime(Math.max(0.001, target), now + glide);
  }

  _connectSource(source, gain, panner, voiceBus) {
    source.connect(gain);
    gain.connect(panner);
    panner.connect(voiceBus);
  }

  // ─── CookUp Core layers ───────────────────────────────────────────────────

  /** Load the Core worklet and the shared DSP module once per engine. */
  async _loadCore() {
    const ctx = this.ctx;
    if (!ctx?.audioWorklet || typeof globalThis.AudioWorkletNode !== 'function') {
      this._markCoreUnavailable();
      return false;
    }
    try {
      let load = coreWorkletLoads.get(ctx);
      if (!load) {
        load = ctx.audioWorklet.addModule(CORE_WORKLET_URL);
        coreWorkletLoads.set(ctx, load);
      }
      const [, dspModule] = await Promise.all([load, loadDspModule()]);
      if (this.destroyed) return false;
      if (!dspModule) throw new Error('CookUp DSP WebAssembly module unavailable');
      this.coreModule = dspModule;
      if (!this.isOfflineContext()) this.coreLayers.forEach((entry) => this._createCoreNode(entry));
      return true;
    } catch (error) {
      coreWorkletLoads.delete(ctx);
      this.coreError = error;
      this._markCoreUnavailable();
      return false;
    }
  }

  _markCoreUnavailable() {
    this.coreUnavailable = true;
    this.coreLayers.forEach((entry) => { entry.failed = true; entry.pending = []; });
  }

  /** Create, update or remove one worklet per enabled Core layer. */
  _syncCoreLayers() {
    const coreLayers = this.patch.layers.filter((layer) => layer.enabled && layer.engine === 'core');
    const wanted = new Set(coreLayers.map((layer) => layer.id));
    this.coreLayers.forEach((entry, layerId) => {
      if (!wanted.has(layerId)) {
        this._disposeCoreLayer(entry);
        this.coreLayers.delete(layerId);
      }
    });
    if (coreLayers.length === 0) return;
    if (this.coreUnavailable && this.lastCorePatchId !== this.patch.id) {
      this.coreUnavailable = false;
      this.coreInit = null;
      this.coreLayers.forEach(entry => { entry.failed = false; });
    }
    this.lastCorePatchId = this.patch.id;
    if (!this.coreInit) this.coreInit = this._loadCore();
    const now = this.ctx.currentTime;
    coreLayers.forEach((layer) => {
      let entry = this.coreLayers.get(layer.id);
      if (!entry) {
        const gain = this.ctx.createGain();
        const pan = this.ctx.createStereoPanner();
        gain.connect(pan);
        pan.connect(this.input);
        entry = {
          layerId: layer.id, gain, pan, node: null, params: null,
          pending: [], offlineEvents: this.isOfflineContext() ? [] : null,
          failed: this.coreUnavailable,
          // Custom wavetables per oscillator slot (A = 0, B = 1).
          tableKeys: [null, null], tables: [null, null], compiledTables: [null, null], tableLoads: []
        };
        this.coreLayers.set(layer.id, entry);
      }
      entry.params = coreParamBlock(layer.core, {
        bendRange: this.patch.performance.pitchBendRange,
        offline: this.isOfflineContext(),
        tempo: this.tempo,
        macroValues: this.patch.macros
      });
      this._syncCoreTables(entry, layer.core);
      entry.gain.gain.setTargetAtTime(layer.level, now, 0.01);
      entry.pan.pan.setTargetAtTime(layer.pan, now, 0.01);
      if (entry.node) entry.node.port.postMessage({ type: 'params', params: entry.params });
      else if (this.coreModule && !this.isOfflineContext()) this._createCoreNode(entry);
    });
  }

  /**
   * Deliver each oscillator's imported wavetable to the layer's engine,
   * fetching it from storage the first time a reopened session needs it.
   */
  _syncCoreTables(entry, core) {
    [core?.oscA, core?.oscB].forEach((osc, slot) => {
      const reference = osc?.table === CORE_CUSTOM_TABLE ? osc.customTable : null;
      const key = reference?.key || null;
      if (entry.tableKeys[slot] === key) return;
      entry.tableKeys[slot] = key;
      if (entry.failed && this.coreModule && !this.coreUnavailable) entry.failed = false;
      entry.tables[slot] = null;
      entry.compiledTables[slot] = null;
      if (!key) return;
      const load = Promise.all([loadCoreTable(reference), loadCompiledCoreTable(reference)]).then(([cycles, compiled]) => {
        if (entry.tableKeys[slot] !== key || this.destroyed) return;
        if (!cycles || (factoryCoreTable(reference) && !compiled)) throw new Error(`Could not load wavetable ${reference.name || key}.`);
        entry.tables[slot] = cycles;
        entry.compiledTables[slot] = compiled;
        if (entry.node) entry.node.port.postMessage({ type: 'table', slot, cycles, compiled });
        else if (this.coreModule && !this.isOfflineContext()) this._createCoreNode(entry);
      }).catch(error => {
        if (entry.tableKeys[slot] !== key || this.destroyed) return;
        this.coreError = error;
        this._failCoreLayer(entry);
      });
      entry.tableLoads.push(load);
    });
  }

  _createCoreNode(entry, events = undefined) {
    if (entry.node || entry.failed || !this.coreModule
      || entry.tableKeys.some((key,slot) => key && !entry.tables[slot])) return;
    try {
      const node = new globalThis.AudioWorkletNode(this.ctx, 'cookup-synth-core', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
        processorOptions: {
          dspModule: this.coreModule,
          params: entry.params,
          ...(events ? { events } : {}),
          tables: entry.tables.map((cycles, slot) => (cycles ? { slot, cycles, compiled: entry.compiledTables[slot] } : null)).filter(Boolean)
        }
      });
      node.port.onmessage = ({ data } = {}) => {
        if (data?.type === 'backend') {
          entry.backend = data.backend;
          if (data.backend === 'unavailable') { this.coreError = new Error('The selected synth engine is unavailable.'); this._failCoreLayer(entry); }
        } else if (data?.type === 'tableLoaded' && !data.loaded) { this.coreError = new Error('The synth wavetable could not be prepared.'); this._failCoreLayer(entry); }
      };
      node.onprocessorerror = () => { this.coreError = new Error('The selected synth stopped rendering.'); this._failCoreLayer(entry); };
      node.connect(entry.gain);
      entry.node = node;
      // Initial tables already arrived in processorOptions. Posting them again
      // would repeat allocation/adoption on the audio thread before first play.
      // Replay notes pressed while loading, dropping ones too stale to play.
      const oldest = this.ctx.currentTime - CORE_PENDING_MAX_AGE;
      const stale = new Set(entry.pending
        .filter((event) => event.type === 'noteOn' && event.time < oldest)
        .map((event) => event.id));
      entry.pending
        .filter((event) => !stale.has(event.id))
        .forEach((event) => node.port.postMessage(event));
      entry.pending = [];
    } catch (error) {
      // Includes engines that cannot structured-clone a WebAssembly.Module.
      this.coreError = error;
      this._failCoreLayer(entry);
    }
  }

  _failCoreLayer(entry) {
    entry.failed = true;
    entry.pending = [];
    if (entry.node) {
      try { entry.node.disconnect(); } catch (_) { /* already disconnected */ }
      entry.node = null;
    }
  }

  _disposeCoreLayer(entry) {
    try { entry.node?.port.postMessage({ type: 'destroy' }); } catch (_) { /* noop */ }
    [entry.node, entry.gain, entry.pan].forEach(disconnectNode);
    entry.node = null;
  }

  _sendCoreEvent(layerId, event) {
    const entry = this.coreLayers.get(layerId);
    if (!entry || entry.failed) return false;
    if (entry.offlineEvents) entry.offlineEvents.push(event);
    else if (entry.node) entry.node.port.postMessage(event);
    else entry.pending.push(event);
    return true;
  }

  _cancelCoreAfter(entry, cutoff) {
    if (entry.offlineEvents) {
      const cancelled = new Set(entry.offlineEvents
        .filter((event) => event.type === 'noteOn' && event.time > cutoff)
        .map((event) => event.id));
      entry.offlineEvents = entry.offlineEvents.filter((event) => !(
        (event.type === 'noteOn' && event.time > cutoff)
        || (event.type === 'noteOff' && cancelled.has(event.id))
      ));
    } else if (entry.node) {
      entry.node.port.postMessage({ type: 'cancelAfter', time: cutoff });
    } else {
      entry.pending = entry.pending.filter((event) => !(event.type === 'noteOn' && event.time > cutoff));
    }
  }

  /**
   * Route one note of a Core layer to its worklet. Returns false when the
   * layer cannot render. The caller reports the failure instead of silently
   * substituting an unrelated sound.
   */
  _startCoreNote(layer, voice, note, now) {
    const entry = this.coreLayers.get(layer.id);
    if (!entry || entry.failed) return false;
    if (voice.coreNoteId == null) voice.coreNoteId = coreVoiceId(voice.id);
    const pitch = note + layer.octave * 12 + layer.semitone + layer.detune / 100;
    this._sendCoreEvent(layer.id, { type: 'noteOn', id: voice.coreNoteId, note: pitch, value: voice.velocity, time: now });
    voice.coreLayerIds.push(layer.id);
    return true;
  }

  /** Offline: build each Core node with its whole schedule, like physical. */
  async _finalizeOfflineCore() {
    if (!this.isOfflineContext() || this.coreLayers.size === 0) return;
    await this.coreInit;
    await Promise.all([...this.coreLayers.values()].flatMap((entry) => entry.tableLoads));
    this.coreLayers.forEach((entry) => {
      if (entry.node || entry.failed || !entry.offlineEvents) return;
      const events = entry.offlineEvents;
      entry.offlineEvents = null;
      this._createCoreNode(entry, events);
    });
  }

  _prepareWavetables() {
    this.patch.layers.filter(layer => layer.enabled && layer.engine === 'wavetable').forEach(layer => {
      const { frames, position } = layer.wavetable;
      const point = position * (frames.length - 1);
      const left = frames[Math.floor(point)];
      const right = frames[Math.min(frames.length - 1, Math.floor(point) + 1)];
      const blend = point - Math.floor(point);
      const real = left.real.map((v,i) => v + (right.real[i]-v)*blend);
      const imag = left.imag.map((v,i) => v + (right.imag[i]-v)*blend);
      const key = JSON.stringify([real,imag]);
      let wave = this.wavetableWaveCache.get(key);
      if (!wave) {
        wave = this.ctx.createPeriodicWave(new Float32Array(real), new Float32Array(imag), { disableNormalization: true });
        if (this.wavetableWaveCache.size >= 64) this.wavetableWaveCache.delete(this.wavetableWaveCache.keys().next().value);
        this.wavetableWaveCache.set(key, wave);
      }
      this.wavetableWaves.set(layer.id, wave);
    });
  }

  _createAnalog(layer, note, velocity, now, voiceBus, voice) {
    const base = this._layerFrequency(note, layer);
    const spread = 3 + Math.abs(layer.detune) + layer.width * 22 + this.patch.macros.Width * 10;
    const unison = Math.max(1, Math.round(layer.unison || 1));
    Array.from({ length: unison }, (_, index) => (
      unison === 1 ? 0 : -spread + ((spread * 2 * index) / (unison - 1))
    )).forEach((detune, oscillatorIndex) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const pan = this.ctx.createStereoPanner();
      osc.type = ['sine', 'triangle', 'square', 'sawtooth'].includes(layer.waveform)
        ? layer.waveform
        : 'sawtooth';
      if (layer.engine === 'wavetable') osc.setPeriodicWave(this.wavetableWaves.get(layer.id));
      this._setPitchedFrequency(osc.frequency, base, layer, now);
      osc.detune.value = detune + this.pitchBend * 100;
      gain.gain.value = layer.level * velocity * (0.68 / Math.sqrt(unison));
      const stereoPosition = unison === 1 ? 0 : -1 + ((2 * oscillatorIndex) / (unison - 1));
      pan.pan.value = clamp(layer.pan + stereoPosition * layer.width * 0.55, -1, 1, 0);
      this._connectSource(osc, gain, pan, voiceBus);
      osc.start(now);
      voice.sources.push(osc);
      voice.pitchParams.push({ param: osc.detune, offset: detune });
      voice.panParams.push({ param: pan.pan, offset: pan.pan.value });
    });
  }

  async _initAfm() {
    try {
      if (!this.ctx.audioWorklet || typeof AudioWorkletNode !== 'function') return false;
      let load = afmWorkletLoads.get(this.ctx);
      if (!load) {
        load = this.ctx.audioWorklet.addModule(dspAssetUrl('/audio/afmInstrumentProcessor.js'));
        afmWorkletLoads.set(this.ctx, load);
      }
      const [, module] = await Promise.all([load, loadPhysicalDspModule()]);
      if (!module || this.destroyed) return false;
      if (!WebAssembly.Module.exports(module).some(entry => entry.name === 'cookup_afm_reset')) throw new Error('Loaded DSP build does not include AFM');
      this.afmModule = module;
      if (!this.isOfflineContext()) {
        this.afmIdleGain = this.ctx.createGain();
        this.afmIdleGain.gain.value = 0;
        this.afmIdleGain.connect(this.input);
        for (let i = 0; i < 48 && !this.destroyed; i++) {
          const node = new AudioWorkletNode(this.ctx, 'cookup-afm', {
            numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1],
            processorOptions: { module, pooled: true }
          });
          const slot = { node, busy: false, ready: false, failed: false };
          slot.readyPromise = new Promise(resolve => { slot.resolveReady = resolve; });
          // Keep idle processors in a pulled audio graph. Never consume a slot
          // until its Rust constructor has explicitly acknowledged readiness.
          node.connect(this.afmIdleGain);
          node.port.onmessage = ({ data }) => {
            if (data?.type === 'ready') {
              slot.ready = true;
              slot.resolveReady(true);
              if (this.afmPool.filter(entry => entry.ready).length === 48) console.info('[CookUp AFM] Rust/WASM voice pool ready');
            } else if (data?.type === 'assetReady') {
              const upload=this.toneUploads.get(data.key);
              if(upload) { upload.ready=true;upload.resolve(true); }
            } else if (data?.type === 'assetError') {
              this.toneUploads.get(data.key)?.resolve(false);
              console.warn('[CookUp Instrument] Asset refused',data.message);
            } else if (data?.type === 'signal' && !this.afmSignalReported) {
              this.afmSignalReported = true;
              console.info('[CookUp Instrument] DSP output', data);
            }
            else if (data?.type === 'error') {
              slot.failed = true;
              slot.resolveReady(false);
              console.error('[CookUp AFM] Processor error', data.message);
            }
          };
          node.onprocessorerror = () => {
            slot.failed = true;
            slot.resolveReady(false);
            console.error('[CookUp AFM] Voice processor failed; basic FM will handle new notes.');
          };
          this.afmPool.push(slot);
          // Spread preparation across turns rather than block playback on a bank load.
          await new Promise(resolve => window.setTimeout(resolve, 0));
        }
      }
      console.info('[CookUp AFM] Voice pool created', { createdVoices: this.afmPool.length, acknowledgedVoices: this.afmPool.filter(slot => slot.ready).length, offline: this.isOfflineContext() });
      return true;
    } catch (error) {
      this.afmError = error;
      afmWorkletLoads.delete(this.ctx);
      return false;
    }
  }

  _createAfm(layer, note, velocity, now, voiceBus, voice) {
    if (!this.afmModule) {
      // Keep notes audible during startup; do not claim this is the Rust voice.
      if (!this.afmFallbackReported) {
        console.warn('[CookUp AFM] Rust engine is not ready; using basic FM until loaded.', this.afmError || 'Loading');
        this.afmFallbackReported = true;
      }
      this._createFm(layer, note, velocity, now, voiceBus, voice);
      return;
    }
    const options = { module: this.afmModule, frequency: this._layerFrequency(note, layer),
      velocity, startTime: now, index: layer.index, ...layer.afm };
    const slot = this.afmPool.find(entry => entry.ready && !entry.failed && !entry.busy);
    if (!slot && !this.isOfflineContext()) {
      if (!this.afmPoolWarning) { console.warn('[CookUp AFM] Prepared voice pool unavailable; basic FM fallback active.'); this.afmPoolWarning = true; }
      this._createFm(layer, note, velocity, now, voiceBus, voice);
      return;
    }
    const node = slot ? slot.node : new AudioWorkletNode(this.ctx, 'cookup-afm', {
      numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1], processorOptions: options
    });
    if (slot) {
      slot.busy = true;
      const noteOptions = { ...options };
      delete noteOptions.module;
      node.port.postMessage({ type: 'note', options: noteOptions });
    }
    node.parameters.get('gate').cancelScheduledValues(this.ctx.currentTime);
    node.parameters.get('gate').setValueAtTime(1, now);
    const gain = this.ctx.createGain();
    const pan = this.ctx.createStereoPanner();
    gain.gain.value = layer.level;
    pan.pan.value = layer.pan;
    this._connectSource(node, gain, pan, voiceBus);
    voice.nodes.push(gain, pan);
    voice.afmNodes.push(node);
    if (!this.afmHitReported) { console.info('[CookUp AFM] First Rust/WASM voice scheduled'); this.afmHitReported = true; }
    voice.pitchParams.push({ param: node.parameters.get('detune'), offset: 0 });
    voice.panParams.push({ param: pan.pan, offset: layer.pan });
  }

  async _uploadToneAsset(key,asset) {
    const existing=this.toneUploads.get(key);
    if(existing)return existing.promise;
    await this.afmReady;
    if(this.isOfflineContext()) {
      this.toneOfflineAssets.set(key,asset);
      this.toneUploads.set(key,{ready:true,promise:Promise.resolve(true)});
      return true;
    }
    const slot=this.afmPool[0];
    if(!slot || this.destroyed)return false;
    const ready=await slot.readyPromise;
    if(!ready || this.destroyed)return false;
    const upload={ready:false};
    upload.promise=new Promise(resolve=>{upload.resolve=resolve;});
    this.toneUploads.set(key,upload);
    slot.node.port.postMessage({type:'asset',key,asset});
    const timeout=setTimeout(()=>upload.resolve(false),8000);
    const accepted=await upload.promise;
    clearTimeout(timeout);
    if(!accepted)this.toneUploads.delete(key);
    return accepted;
  }

  _prepareToneAssets() {
    this.patch.layers.filter(layer=>layer.enabled && ['wavetable','spectral','granular'].includes(layer.engine)).forEach(layer=>{
      const isGrain=layer.engine==='granular';
      const zone=isGrain ? layer.sampleZones[0] : null;
      const key=isGrain ? `sample-${zone?.bufferId||zone?.url||''}` : toneTableKey(layer.wavetable);
      this.toneLayerAssets.set(layer.id,key);
      if(this.toneJobs.has(key))return;
      const job=(async()=>{
        try {
          let asset;
          if(isGrain) {
            await this.sampleLoadPromise;
            const buffer=this.sampleBuffers.get(zone?.bufferId||zone?.url);
            if(!buffer)return false;
            asset=await compileToneSample(buffer,key);
          } else asset=await compileToneTable(layer.wavetable);
          return await this._uploadToneAsset(key,asset);
        } catch(error) { console.warn('[CookUp Instrument] Advanced asset preparation failed',error?.message);return false; }
      })();
      this.toneJobs.set(key,job);
      job.then(ok=>{if(!ok)this.toneJobs.delete(key);});
    });
  }

  _toneValues(layer) {
    return {position:layer.wavetable?.position??.5,warp:layer.wavetable?.warp??0,
      stretch:layer.spectral?.stretch??1,tilt:layer.spectral?.tilt??0,
      grainPosition:layer.granular?.position??.5,grainSize:layer.granular?.size??.09,
      grainDensity:layer.granular?.density??18,grainPitch:layer.granular?.pitch??0};
  }

  _createTone(layer,note,velocity,now,voiceBus,voice) {
    const key=this.toneLayerAssets.get(layer.id);
    const slot=this.afmPool.find(entry=>entry.ready && !entry.failed && !entry.busy);
    const ready=this.toneUploads.get(key)?.ready;
    if(!ready || (!slot && !this.isOfflineContext())) {
      if(!this.toneFallbackReported) {console.warn('[CookUp Instrument] Advanced worklet unavailable/pending: native fallback active');this.toneFallbackReported=true;}
      if(layer.engine==='granular')this._createSample(layer,note,velocity,now,voiceBus,voice);
      else if(layer.engine==='spectral')this._createAdditive(layer,note,velocity,now,voiceBus,voice);
      else this._createAnalog(layer,note,velocity,now,voiceBus,voice);
      return;
    }
    const zone=layer.sampleZones[0];
    const options={mode:layer.engine,assetKey:key,frequency:this._layerFrequency(note,layer),note,velocity,startTime:now,
      unison:Math.min(8,layer.unison),unisonDetune:3+Math.abs(layer.detune)+layer.width*22,
      playbackRate:zone ? 2**((note-zone.rootNote+layer.octave*12+layer.semitone+(layer.detune+zone.tune)/100)/12):1,
      warpMode:layer.wavetable?.warpMode||'none',scanRate:layer.wavetable?.scanRate||.3,scanDepth:layer.wavetable?.scanDepth||0,
      toneModulation:layer.toneModulation,granular:layer.granular,spectral:layer.spectral,envelope:this.patch.ampEnvelope};
    let node;
    if(this.isOfflineContext()) {
      node=new AudioWorkletNode(this.ctx,'cookup-afm',{numberOfInputs:0,numberOfOutputs:1,outputChannelCount:[1],processorOptions:{...options,module:this.afmModule,asset:this.toneOfflineAssets.get(key)}});
    } else { slot.busy=true;node=slot.node;node.port.postMessage({type:'note',options}); }
    node.parameters.get('gate').cancelScheduledValues(this.ctx.currentTime);
    node.parameters.get('gate').setValueAtTime(1,now);
    Object.entries(this._toneValues(layer)).forEach(([name,value])=>{
      node.parameters.get(name).cancelScheduledValues(this.ctx.currentTime);
      node.parameters.get(name).setValueAtTime(value,now);
    });
    const gain=this.ctx.createGain(),pan=this.ctx.createStereoPanner();
    gain.gain.value=layer.level*velocity*.55;pan.pan.value=layer.pan;
    this._connectSource(node,gain,pan,voiceBus);
    voice.nodes.push(gain,pan);voice.afmNodes.push(node);voice.toneNodes.push({node,layerId:layer.id});
    voice.pitchParams.push({param:node.parameters.get('detune'),offset:0});
    voice.panParams.push({param:pan.pan,offset:layer.pan});
  }

  _recycleAfm(node) {
    node.disconnect();
    const slot = this.afmPool.find(entry => entry.node === node);
    if (slot && !this.destroyed && !slot.failed) {
      node.connect(this.afmIdleGain);
      node.port.postMessage({ type: 'reset' });
      slot.busy = false;
    } else node.port.postMessage('dispose');
  }

  _createFm(layer, note, velocity, now, voiceBus, voice) {
    const base = this._layerFrequency(note, layer);
    const carrier = this.ctx.createOscillator();
    const modulator = this.ctx.createOscillator();
    const modGain = this.ctx.createGain();
    const gain = this.ctx.createGain();
    const pan = this.ctx.createStereoPanner();
    carrier.type = 'sine';
    modulator.type = 'sine';
    this._setPitchedFrequency(carrier.frequency, base, layer, now);
    this._setPitchedFrequency(modulator.frequency, base * layer.ratio, layer, now, layer.ratio);
    modGain.gain.value = base * layer.index * (0.035 + layer.tone * 0.055 + this.patch.macros.Metal * 0.2);
    gain.gain.value = layer.level * velocity * 0.48;
    pan.pan.value = layer.pan;
    modulator.connect(modGain);
    modGain.connect(carrier.frequency);
    if (layer.feedback > 0.0001) {
      const feedbackGain = this.ctx.createGain();
      feedbackGain.gain.value = base * layer.feedback * 0.18;
      modulator.connect(feedbackGain);
      feedbackGain.connect(modulator.frequency);
      voice.nodes.push(feedbackGain);
    }
    this._connectSource(carrier, gain, pan, voiceBus);
    carrier.start(now);
    modulator.start(now);
    voice.sources.push(carrier, modulator);
    voice.pitchParams.push(
      { param: carrier.detune, offset: 0 },
      { param: modulator.detune, offset: 0 }
    );
    voice.panParams.push({ param: pan.pan, offset: pan.pan.value });
  }

  _createAdditive(layer, note, velocity, now, voiceBus, voice) {
    const base = this._layerFrequency(note, layer);
    const brightness = 0.18 + layer.tone * 0.65 + this.patch.macros.Air * 0.25;
    const harmonicLevels = Array.isArray(layer.harmonics) ? layer.harmonics : [];
    harmonicLevels.forEach((harmonicLevel, index) => {
      if (harmonicLevel <= 0.0001) return;
      const harmonic = index + 1;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const pan = this.ctx.createStereoPanner();
      const detune = layer.detune + (index % 2 === 0 ? -1 : 1) * layer.width * index * 1.8;
      osc.type = 'sine';
      this._setPitchedFrequency(osc.frequency, base * harmonic, layer, now, harmonic);
      osc.detune.value = detune + this.pitchBend * 100;
      const tilt = Math.pow(0.35 + layer.harmonicTilt * 0.65, index * 0.35);
      gain.gain.value = layer.level * velocity * harmonicLevel * Math.pow(brightness, index * 0.18) * tilt * 0.42;
      pan.pan.value = clamp(layer.pan + (index % 2 === 0 ? -1 : 1) * layer.width * 0.35, -1, 1, 0);
      this._connectSource(osc, gain, pan, voiceBus);
      osc.start(now);
      voice.sources.push(osc);
      voice.pitchParams.push({ param: osc.detune, offset: detune });
      voice.panParams.push({ param: pan.pan, offset: pan.pan.value });
    });
  }

  _createTexture(layer, note, velocity, now, voiceBus, voice) {
    const source = this.ctx.createBufferSource();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();
    const pan = this.ctx.createStereoPanner();
    source.buffer = this.noiseBuffers[layer.noiseColor] || this.noiseBuffers.pink;
    source.loop = true;
    const pitchedRate = (0.65 + layer.density * 1.4) * (this._layerFrequency(note, layer) / midiToFrequency(60));
    source.playbackRate.value = clamp(pitchedRate, 0.125, 8, 1);
    filter.type = 'bandpass';
    filter.frequency.value = clamp(this._layerFrequency(note, layer) * (1.5 + layer.tone * 6), 80, 16000, 1200);
    filter.Q.value = 0.6 + layer.size * 8;
    gain.gain.value = layer.level * velocity * 0.32;
    pan.pan.value = layer.pan;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(pan);
    pan.connect(voiceBus);
    source.start(now);
    voice.sources.push(source);
    voice.panParams.push({ param: pan.pan, offset: pan.pan.value });
  }

  _createResonator(layer, note, velocity, now, voiceBus, voice) {
    const base = this._layerFrequency(note, layer);
    const partials = Array.isArray(layer.harmonics) ? layer.harmonics : [];
    partials.forEach((partialLevel, index) => {
      if (partialLevel <= 0.0001) return;
      const harmonic = index + 1;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const pan = this.ctx.createStereoPanner();
      const stretchedHarmonic = harmonic * (1 + layer.inharmonicity * index * 0.012);
      const frequency = Math.min(20000, base * stretchedHarmonic);
      this._setPitchedFrequency(osc.frequency, frequency, layer, now, stretchedHarmonic);
      osc.type = 'sine';
      gain.gain.setValueAtTime(Math.max(0.0001, layer.level * velocity * partialLevel * 0.42), now);
      const decay = 0.08 + (1 - layer.damping) * 7 / Math.sqrt(harmonic);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + decay);
      pan.pan.value = clamp(layer.pan + (index % 2 ? 1 : -1) * layer.width * 0.38, -1, 1, 0);
      this._connectSource(osc, gain, pan, voiceBus);
      osc.start(now);
      osc.stop(now + decay + 0.08);
      voice.sources.push(osc);
      voice.nodes.push(gain, pan);
      voice.pitchParams.push({ param: osc.detune, offset: 0 });
      voice.panParams.push({ param: pan.pan, offset: pan.pan.value });
    });
  }

  _createPhysical(layer, note, velocity, now, voiceBus, voice) {
    if (['piano', 'pluck', 'bowed', 'wind', 'reed', 'brass', 'mallet', 'bell'].includes(layer.physicalModel) && this._usesPhysicalWorklet()) {
      const workletId = `${voice.id}:${layer.id || 'piano'}`;
      this._sendPhysicalEvent({
        type: 'noteOn',
        id: workletId,
        note,
        velocity,
        time: now,
        options: {
          physicalModel: layer.physicalModel,
          level: layer.level,
          pan: layer.pan,
          tone: layer.tone,
          width: layer.width,
          octave: layer.octave,
          semitone: layer.semitone,
          detune: layer.detune,
          inharmonicity: layer.inharmonicity,
          damping: layer.damping,
          exciter: layer.exciter,
          bodySize: layer.bodySize,
          bodyDecay: layer.bodyDecay,
          material: layer.material,
          pickPosition: layer.pickPosition,
          breath: layer.breath,
          articulation: layer.articulation,
          bodyMix: layer.bodyMix,
          sympathetic: layer.sympathetic,
          coupling: layer.coupling,
          mechanics: layer.mechanics,
          variation: layer.variation,
          micPosition: layer.micPosition
        }
      });
      voice.workletNoteIds.push(workletId);
      return;
    }
    const profile = PHYSICAL_PROFILES[layer.physicalModel] || PHYSICAL_PROFILES.piano;
    const base = this._layerFrequency(note, layer);
    const source = this.ctx.createBufferSource();
    const exciterGain = this.ctx.createGain();
    const exciterFilter = this.ctx.createBiquadFilter();
    const tonalAmount = profile.anchor ? 0.08 + (1 - layer.breath) * 0.16 : 0;
    const burstDuration = profile.burst * (0.55 + layer.bodySize * 1.25);
    const decaySeconds = (0.12 + Math.pow(layer.bodyDecay, 1.7) * 8.5) * profile.decay;
    const excitation = layer.level * velocity * (0.12 + layer.exciter * 0.4 + layer.breath * 0.18);

    source.buffer = this.noiseBuffers[layer.noiseColor] || this.noiseBuffers.pink;
    source.loop = profile.sustain;
    source.playbackRate.value = 0.75 + layer.material * 1.1;
    exciterFilter.type = 'lowpass';
    exciterFilter.frequency.value = clamp(base * (3 + layer.tone * 18), 250, 18000, 5000);
    exciterFilter.Q.value = 0.45 + layer.material * 1.8;
    exciterGain.gain.setValueAtTime(0.0001, now);
    exciterGain.gain.exponentialRampToValueAtTime(Math.max(0.0001, excitation), now + 0.002 + (profile.sustain ? 0.025 : 0));
    if (profile.sustain) {
      exciterGain.gain.exponentialRampToValueAtTime(Math.max(0.0001, excitation * (0.3 + layer.breath * 0.7)), now + 0.09);
    } else {
      exciterGain.gain.exponentialRampToValueAtTime(0.0001, now + Math.max(0.012, burstDuration));
    }
    source.connect(exciterFilter);
    exciterFilter.connect(exciterGain);
    source.start(now, ((note * 37) % 100) / 100);
    if (!profile.sustain) source.stop(now + Math.max(0.02, burstDuration) + 0.02);
    voice.sources.push(source);
    voice.nodes.push(exciterGain, exciterFilter);

    profile.ratios.forEach((ratio, index) => {
      const frequency = base * ratio * (1 + layer.inharmonicity * index * index * 0.0018);
      const safeNyquist = this.ctx.sampleRate >= 8000 ? Math.min(20000, this.ctx.sampleRate * 0.46) : 20000;
      if (frequency >= safeNyquist) return;
      const mode = this.ctx.createBiquadFilter();
      const modeGain = this.ctx.createGain();
      const pan = this.ctx.createStereoPanner();
      const positionResponse = Math.max(0.08, Math.abs(Math.sin(Math.PI * ratio * layer.pickPosition)));
      const highLoss = Math.pow(1 - layer.damping * 0.58, index);
      const level = layer.level * velocity * profile.gains[index] * positionResponse * highLoss * (0.28 + layer.material * 0.24);
      mode.type = 'bandpass';
      mode.frequency.setValueAtTime(frequency, now);
      mode.detune.value = layer.detune + this.pitchBend * 100;
      mode.Q.value = 7 + layer.material * 32 + (1 - layer.damping) * 18 + index * 1.4;
      modeGain.gain.setValueAtTime(Math.max(0.0001, level), now);
      if (!profile.sustain) {
        const modeDecay = Math.max(0.045, decaySeconds / Math.pow(index + 1, 0.38 + layer.damping * 0.8));
        modeGain.gain.exponentialRampToValueAtTime(0.0001, now + modeDecay);
      }
      pan.pan.value = clamp(layer.pan + (index % 2 ? 1 : -1) * layer.width * Math.min(0.42, index * 0.07), -1, 1, 0);
      exciterGain.connect(mode);
      mode.connect(modeGain);
      modeGain.connect(pan);
      pan.connect(voiceBus);
      voice.nodes.push(mode, modeGain, pan);
      voice.pitchParams.push({ param: mode.detune, offset: layer.detune });
      voice.panParams.push({ param: pan.pan, offset: pan.pan.value });
    });

    if (tonalAmount > 0.0001) {
      const anchor = this.ctx.createOscillator();
      const anchorGain = this.ctx.createGain();
      const anchorPan = this.ctx.createStereoPanner();
      anchor.type = layer.physicalModel === 'brass' || layer.physicalModel === 'reed' ? 'triangle' : 'sine';
      this._setPitchedFrequency(anchor.frequency, base, layer, now);
      anchor.detune.value = layer.detune + this.pitchBend * 100;
      anchorGain.gain.setValueAtTime(Math.max(0.0001, layer.level * velocity * tonalAmount), now);
      if (!profile.sustain) anchorGain.gain.exponentialRampToValueAtTime(0.0001, now + decaySeconds);
      anchorPan.pan.value = layer.pan;
      this._connectSource(anchor, anchorGain, anchorPan, voiceBus);
      anchor.start(now);
      if (!profile.sustain) anchor.stop(now + decaySeconds + 0.05);
      voice.sources.push(anchor);
      voice.nodes.push(anchorGain, anchorPan);
      voice.pitchParams.push({ param: anchor.detune, offset: layer.detune });
      voice.panParams.push({ param: anchorPan.pan, offset: anchorPan.pan.value });
    }
  }

  _selectSampleZone(layer, note, velocity, trigger = 'attack') {
    const hosted = this.hostedLayers.get(layer.id);
    if (hosted?.unavailable) return null; // nothing to stream; noteOn plays the modelled fallback
    const zones = this._layerZones(layer);
    const candidates = zones
      .filter((zone) => zone.trigger === trigger
        && note >= zone.loNote && note <= zone.hiNote
        && velocity >= zone.loVelocity && velocity <= zone.hiVelocity
        // Hosted recordings stream in on demand; local ones must already be loaded.
        && (hosted ? true : this.sampleBuffers.has(zone.bufferId || zone.url)))
      .sort((a, b) => a.roundRobin - b.roundRobin || a.rootNote - b.rootNote);
    if (candidates.length === 0) return null;
    const key = `${layer.id}:${trigger}:${note}:${Math.round(velocity * 16)}`;
    const position = this.sampleRoundRobin.get(key) || 0;
    this.sampleRoundRobin.set(key, position + 1);
    let zone = candidates[position % candidates.length];
    let buffer = null;
    if (hosted && !this._zoneBuffer(layer, zone)) {
      if (this.isOfflineContext()) throw new Error(`Lossless recording ${zone.sampleId} was not prepared; export stopped.`);
      if (hosted.manifest) this.hostedStore.request(this.ctx, hosted.manifest, zone.sampleId, PRIORITY.now);
      // Until it arrives: the nearest full recording, else this zone's preview, else the nearest preview.
      const wanted = zone;
      const full = this._hostedFallback(layer, zones, wanted, note, velocity, trigger);
      if (full) zone = full;
      else if (this._zoneBuffer(layer, wanted, 'preview')) buffer = this._zoneBuffer(layer, wanted, 'preview');
      else {
        zone = this._hostedFallback(layer, zones, wanted, note, velocity, trigger, 'preview');
        if (!zone) return null;
        buffer = this._zoneBuffer(layer, zone, 'preview');
      }
    }
    return { zone, buffer, position, alternatives: candidates.length };
  }

  _createSample(layer, note, velocity, now, voiceBus, voice, trigger = 'attack', heldSeconds = 0) {
    const pick = this._selectSampleZone(layer, note, velocity, trigger);
    if (!pick) return false;
    const { zone } = pick;
    // One recording for this note: vary each play slightly so repeats are never identical.
    // Instruments with real round-robin recordings already vary.
    const variation = trigger === 'attack' && pick.alternatives === 1
      ? noteVariation({ layerId: layer.id, note, playCount: pick.position, amount: layer.humanize ?? HUMANIZE_DEFAULT })
      : noteVariation({ amount: 0 });
    const bufferKey = zone.bufferId || zone.url;
    const original = pick.buffer || this._zoneBuffer(layer, zone);
    if (!original) return false;
    const source = this.ctx.createBufferSource();
    const gain = this.ctx.createGain();
    const pan = this.ctx.createStereoPanner();
    const root = clamp(zone.rootNote, 0, 127, 60);
    const coarse = layer.octave * 12 + layer.semitone + (note - root) * (zone.pitchKeytrack ?? 1);
    const cents = layer.detune + zone.tune;
    // Far above the root, play a band-limited copy prepared nearer the note's pitch.
    const shift = coarse + cents / 100;
    const copySemitones = pitchCopyFor(shift);
    const copy = copySemitones ? this.pitchCopies.get(pitchCopyKey(bufferKey, copySemitones)) : null;
    const played = copy || original;
    const timeScale = copy ? 2 ** (-copySemitones / 12) : 1; // copy seconds per original second
    const baseRate = Math.pow(2, (shift - (copy ? copySemitones : 0)) / 12) * variation.rateFactor;
    const duration = Number(original.duration) || original.length / (original.sampleRate || this.ctx.sampleRate);
    const start = clamp(zone.start || layer.sampleStart, 0, Math.max(0, duration - 0.001), 0);
    const configuredEnd = zone.end ?? layer.sampleEnd;
    const end = configuredEnd == null || configuredEnd <= start
      ? duration
      : clamp(configuredEnd, start + 0.001, duration, duration);
    const loopEnabled = zone.loopEnabled || layer.loopEnabled;
    const loopStart = clamp(zone.loopStart || layer.loopStart, start, Math.max(start, end - 0.002), start);
    const requestedLoopEnd = zone.loopEnd || layer.loopEnd;
    const loopEnd = requestedLoopEnd > loopStart
      ? clamp(requestedLoopEnd, loopStart + 0.002, end, end)
      : end;
    const loopCrossfade = zone.loopCrossfade ?? layer.loopCrossfade;
    source.buffer = loopEnabled
      ? createCrossfadedLoopBuffer(this.ctx, played, loopStart * timeScale, loopEnd * timeScale, loopCrossfade * timeScale)
      : played;
    source.loop = loopEnabled;
    if (loopEnabled) {
      source.loopStart = loopStart * timeScale;
      source.loopEnd = loopEnd * timeScale;
    }
    source.playbackRate.setValueAtTime(baseRate * Math.pow(2, this.pitchBend / 12), now);
    if (this.patch.performance.glide > 0 && this.lastPlayedNote != null) {
      const previousRate = baseRate * Math.pow(2, (this.lastPlayedNote - note + this.pitchBend) / 12);
      source.playbackRate.setValueAtTime(Math.max(0.001, previousRate), now);
      source.playbackRate.exponentialRampToValueAtTime(
        Math.max(0.001, baseRate * Math.pow(2, this.pitchBend / 12)),
        now + this.patch.performance.glide
      );
    }
    // SFZ zones follow their own velocity tracking (curve v²); others keep CookUp's linear velocity.
    const velocityGain = zone.velocityTrack == null ? velocity : Math.max(0, 1 + zone.velocityTrack * (velocity * velocity - 1));
    // A release sample is quieter the longer the key was held (SFZ rt_decay, dB per second).
    const releaseFade = trigger === 'release' && zone.releaseDecay ? 10 ** (-zone.releaseDecay * Math.max(0, heldSeconds) / 20) : 1;
    gain.gain.value = layer.level * velocityGain * zone.gain * variation.gainFactor * releaseFade;
    if (trigger === 'attack' && zone.release != null) voice.releaseOverride = Math.max(voice.releaseOverride || 0, zone.release);
    if (trigger === 'attack') this._requestHostedReleases(layer, note, velocity);
    pan.pan.value = clamp(layer.pan + zone.pan, -1, 1, 0);
    // One recording for every velocity: soft notes get darker, as a played instrument does.
    const toneCutoff = hasVelocityLayers(this._layerZones(layer))
      ? null
      : velocityToneCutoff(note + layer.octave * 12 + layer.semitone, velocity, layer.velocityTone ?? VELOCITY_TONE_DEFAULT, this.ctx.sampleRate);
    if (toneCutoff) {
      const tone = this.ctx.createBiquadFilter();
      tone.type = 'lowpass';
      tone.frequency.value = Math.min(toneCutoff * variation.toneFactor, this.ctx.sampleRate * 0.45);
      tone.Q.value = 0.6;
      source.connect(tone);
      tone.connect(gain);
      voice.nodes.push(tone);
    } else {
      source.connect(gain);
    }
    gain.connect(pan);
    pan.connect(voiceBus);
    if (loopEnabled) source.start(now, start * timeScale);
    else source.start(now, start * timeScale, Math.max(0.001, (end - start) * timeScale));
    voice.sources.push(source);
    voice.nodes.push(gain, pan);
    voice.playbackRateParams.push({ param: source.playbackRate, baseRate });
    voice.panParams.push({ param: pan.pan, offset: pan.pan.value });
    return true;
  }

  /** Stream a key's release samples while it is held, so they are ready when it lifts. */
  _requestHostedReleases(layer, note, velocity) {
    const hosted = this.hostedLayers.get(layer.id);
    if (!hosted?.manifest) return;
    hosted.zones.forEach((zone) => {
      if (zone.trigger === 'release' && note >= zone.loNote && note <= zone.hiNote
        && velocity >= zone.loVelocity && velocity <= zone.hiVelocity && !this._zoneBuffer(layer, zone)) {
        this.hostedStore.request(this.ctx, hosted.manifest, zone.sampleId, PRIORITY.now, this.isOfflineContext() ? 'lossless' : null);
      }
    });
  }

  /** The damper mechanism's own sound as the pedal goes down or up, where the instrument recorded it. */
  _playPedalSamples(down) {
    const now = this.ctx.currentTime;
    this.patch.layers
      .filter((layer) => layer.enabled && layer.engine === 'sample' && layer.level > 0.0001)
      .forEach((layer) => {
        const pedalVoice = { sources: [], nodes: [], playbackRateParams: [], panParams: [] };
        if (!this._createSample(layer, 60, 0.8, now, this.input, pedalVoice, down ? 'pedal-down' : 'pedal-up')) return;
        pedalVoice.sources.forEach((source) => {
          source.onended = () => pedalVoice.nodes.forEach(disconnectNode);
        });
      });
  }

  _playReleaseSamples(voice, now) {
    if (voice.releaseSamplesTriggered) return;
    voice.releaseSamplesTriggered = true;
    this.patch.layers
      .filter((layer) => layer.enabled && layer.engine === 'sample' && layer.level > 0.0001)
      .forEach((layer) => {
        const releaseVoice = { sources: [], nodes: [], playbackRateParams: [], panParams: [] };
        const held = Math.max(0, now - (voice.startedAt ?? now));
        if (!this._createSample(layer, voice.note, voice.velocity, now, this.input, releaseVoice, 'release', held)) return;
        releaseVoice.sources.forEach((source) => {
          source.onended = () => releaseVoice.nodes.forEach(disconnectNode);
        });
      });
  }

  _createVoiceLfo(config, voice, now) {
    if (!config?.enabled || config.depth <= 0.0001) return;
    const lfo = this.ctx.createOscillator();
    const lfoGain = this.ctx.createGain();
    lfo.type = config.shape || 'sine';
    const rate = config.sync
      ? (this.tempo / 60) / Math.max(0.02, config.rate)
      : config.rate;
    lfo.frequency.value = clamp(rate * (0.7 + this.patch.macros.Motion * 1.6), 0.02, 30, 0.3);
    const modulationDepth = clamp(config.depth + this.patch.macros.Motion * 0.18, 0, 1, 0.1);
    lfoGain.gain.value = config.destination === 'pitch'
      ? modulationDepth * 100
      : config.destination === 'amplitude'
        ? modulationDepth * 0.12
        : config.destination === 'pan'
          ? modulationDepth * 0.9
          : this.patch.filter.cutoff * modulationDepth * 0.45;
    lfo.connect(lfoGain);
    if (config.destination === 'pitch') {
      voice.pitchParams.forEach(({ param }) => lfoGain.connect(param));
    } else if (config.destination === 'amplitude') {
      lfoGain.connect(voice.envelope.gain);
    } else if (config.destination === 'pan') {
      voice.panParams.forEach(({ param }) => lfoGain.connect(param));
    } else {
      lfoGain.connect(voice.filter.frequency);
      if (voice.filter2) lfoGain.connect(voice.filter2.frequency);
    }
    lfo.start(now);
    voice.sources.push(lfo);
    voice.nodes.push(lfoGain);
  }

  _updateVoiceTone(voice, scheduleEnvelope = false, scheduledTime = null) {
    if (!voice?.filter) return;
    const now = Math.max(this.ctx.currentTime, Number(scheduledTime) || 0);
    const body = this.patch.macros.Body;
    const air = this.patch.macros.Air;
    const keyMultiplier = Math.pow(2, ((voice.note - 60) / 12) * this.patch.filter.keyTrack);
    const cutoff = clamp(
      this.patch.filter.cutoff * keyMultiplier * (0.58 + body * 0.64 + air * 0.35),
      30,
      20000,
      3000
    );
    voice.baseCutoff = cutoff;
    voice.filter.frequency.cancelScheduledValues(now);
    if (scheduleEnvelope) {
      const envelope = this.patch.filterEnvelope;
      const amount = clamp(envelope.amount, -1, 1, 0.5);
      const start = cutoff;
      const peak = clamp(cutoff * Math.pow(2, amount * 4), 20, 20000, cutoff);
      const sustain = clamp(
        cutoff * Math.pow(2, amount * 4 * clamp(envelope.sustain, 0, 1, 0.25)),
        20,
        20000,
        cutoff
      );
      const attackEnd = now + Math.max(0.001, envelope.attack);
      const decayEnd = attackEnd + Math.max(0.001, envelope.decay);
      voice.filter.frequency.setValueAtTime(start, now);
      voice.filter.frequency.exponentialRampToValueAtTime(peak, attackEnd);
      voice.filter.frequency.exponentialRampToValueAtTime(sustain, decayEnd);
    } else {
      voice.filter.frequency.setTargetAtTime(cutoff, now, 0.025);
    }
    voice.filter.Q.setTargetAtTime(this.patch.filter.resonance + this.patch.macros.Metal * 1.4, now, 0.025);
  }

  noteOn(note, velocity = 0.82, id = null, scheduledTime = null) {
    for (const layer of this.patch.layers) {
      const entry = this.hostedLayers.get(layer.id);
      if (layer.enabled && layer.level > 0.0001 && entry?.strict && entry.unavailable) throw entry.error;
    }
    if (this.patch.layers.some(layer => layer.enabled && layer.level > 0.0001 && layer.engine === 'core'
      && (this.coreUnavailable || this.coreLayers.get(layer.id)?.failed))) {
      throw this.coreError || new Error('The selected synth could not load. Please retry.');
    }
    const midiNote = Math.round(clamp(note, 0, 127, 60));
    const voiceId = id || `note:${midiNote}`;
    const now = Math.max(this.ctx.currentTime, Number(scheduledTime) || 0);
    if (this.voices.has(voiceId)) {
      this.noteOff(voiceId, true);
      this.voiceOrder = this.voiceOrder.filter((entry) => entry !== voiceId);
    }
    // Voices that have already finished by the time this note starts are not
    // sounding and must not count against polyphony. Without this, an offline
    // export — which schedules a whole song in one synchronous pass, so the
    // realtime cleanup timers never run — grows voiceOrder to the full note
    // count and steals notes that have not been heard yet.
    this._retireFinishedVoices(now);
    const voiceLimit = this._effectiveVoiceLimit(this.patch);
    while (this.voiceOrder.length >= voiceLimit) {
      const stolenVoiceId = this.voiceOrder.shift();
      // Release the stolen voice where the new one begins, not at the audio
      // clock: offline those differ by the whole length of the song, which
      // silenced the stolen note instead of ending it.
      this.noteOff(stolenVoiceId, true, now);
    }

    const voiceBus = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    const filter2 = this.patch.filter2.enabled ? this.ctx.createBiquadFilter() : null;
    const envelope = this.ctx.createGain();
    const voice = {
      id: voiceId,
      note: midiNote,
      velocity: clamp(velocity, 0.01, 1, 0.82),
      startedAt: now,
      sources: [],
      pitchParams: [],
      playbackRateParams: [],
      panParams: [],
      workletNoteIds: [],
      coreLayerIds: [],
      coreNoteId: null,
      layerEnvelopes: [],
      toneNodes: [],
      afmNodes: [],
      nodes: [voiceBus, filter, ...(filter2 ? [filter2] : []), envelope],
      filter,
      filter2,
      envelope,
      released: false,
      releaseSamplesTriggered: false
    };
    filter.type = this.patch.filter.enabled ? this.patch.filter.type : 'allpass';
    filter.frequency.value = this.patch.filter.cutoff;
    filter.Q.value = this.patch.filter.resonance;
    if (filter2) {
      filter2.type = this.patch.filter2.type;
      filter2.frequency.value = this.patch.filter2.cutoff;
      filter2.Q.value = this.patch.filter2.resonance;
    }
    envelope.gain.value = 0.0001;
    voiceBus.connect(filter);
    if (filter2) {
      filter.connect(filter2);
      filter2.connect(envelope);
    } else filter.connect(envelope);
    envelope.connect(this.input);

    this.sustainedVoiceIds.delete(voiceId);
    let webAudioLayers = 0;
    this.patch.layers.filter((layer) => layer.enabled && layer.level > 0.0001).forEach((layer) => {
      if (layer.engine === 'core' && this._startCoreNote(layer, voice, midiNote, now)) return;
      webAudioLayers += 1;
      let layerDestination = voiceBus;
      if (layer.envelope?.enabled) {
        const layerEnvelope = this.ctx.createGain();
        const layerEnv = layer.envelope;
        layerEnvelope.gain.setValueAtTime(0.0001, now);
        layerEnvelope.gain.exponentialRampToValueAtTime(1, now + layerEnv.attack);
        layerEnvelope.gain.exponentialRampToValueAtTime(Math.max(0.0001, layerEnv.sustain), now + layerEnv.attack + layerEnv.decay);
        layerEnvelope.connect(voiceBus);
        voice.nodes.push(layerEnvelope);
        voice.layerEnvelopes.push({ node: layerEnvelope, release: layerEnv.release });
        layerDestination = layerEnvelope;
      }
      if (layer.engine === 'afm') this._createAfm(layer, midiNote, voice.velocity, now, layerDestination, voice);
      else if (layer.engine === 'fm') this._createFm(layer, midiNote, voice.velocity, now, layerDestination, voice);
      else if (layer.engine === 'additive') this._createAdditive(layer, midiNote, voice.velocity, now, layerDestination, voice);
      else if (layer.engine === 'texture') this._createTexture(layer, midiNote, voice.velocity, now, layerDestination, voice);
      else if (layer.engine === 'resonator') this._createResonator(layer, midiNote, voice.velocity, now, layerDestination, voice);
      else if (layer.engine === 'physical') this._createPhysical(layer, midiNote, voice.velocity, now, layerDestination, voice);
      else if (['wavetable','spectral','granular'].includes(layer.engine)) this._createTone(layer, midiNote, voice.velocity, now, layerDestination, voice);
      else if (layer.engine === 'sample' && this.hostedLayers.get(layer.id)?.unavailable) {
        // Hosted library unreachable: play CookUp's modelled instrument of the same family instead of silence.
        const { library, instrument } = layer.hostedInstrument;
        this._createPhysical({ ...layer, engine: 'physical', physicalModel: hostedFallbackModel(library, instrument) }, midiNote, voice.velocity, now, layerDestination, voice);
      } else if (layer.engine === 'sample') this._createSample(layer, midiNote, voice.velocity, now, layerDestination, voice);
      else if (layer.engine === 'core') throw this.coreError || new Error('The selected synth is not ready; retry after loading.');
      else this._createAnalog(layer, midiNote, voice.velocity, now, layerDestination, voice);
    });

    // Core layers modulate inside Rust; per-note Web Audio LFOs would only
    // spend oscillators on a silent voice bus.
    if (webAudioLayers > 0) {
      this._createVoiceLfo(this.patch.lfo, voice, now);
      this._createVoiceLfo(this.patch.lfo2, voice, now);
    }

    const env = this.patch.ampEnvelope;
    const peak = (1 - env.velocity) + voice.velocity * env.velocity;
    const punch = this.patch.macros.Punch;
    const attack = Math.max(0.001, env.attack * (1.15 - punch * 0.8));
    envelope.gain.setValueAtTime(0.0001, now);
    envelope.gain.exponentialRampToValueAtTime(Math.max(0.001, peak), now + attack);
    envelope.gain.exponentialRampToValueAtTime(
      Math.max(0.001, peak * env.sustain),
      now + attack + Math.max(0.001, env.decay)
    );
    this._updateVoiceTone(voice, true, now);
    // Step modulation is scheduled as AudioParam automation rather than driven
    // by a wall-clock timer. setInterval is neither sample accurate live nor
    // able to run at all during an offline render — the whole mix finishes
    // before the first tick would fire — so exports silently lost the movement.
    // noteOff cancels this automation at the release, bounding the schedule.
    const stepMod = this.patch.stepMod;
    const stepValues = stepMod?.enabled && webAudioLayers > 0 ? (stepMod.steps || []) : [];
    if (stepValues.length > 0) {
      const depth = clamp(stepMod.depth, 0, 1, 0.3);
      const baseCutoff = voice.baseCutoff || this.patch.filter.cutoff;
      const intervalSeconds = Math.max(0.02, (60 / Math.max(30, this.tempo)) * stepMod.rate);
      for (let step = 0; step < STEP_MOD_SCHEDULED_STEPS; step += 1) {
        const value = clamp(stepValues[step % stepValues.length], 0, 1, 0.5);
        const target = clamp(baseCutoff * (1 + (value - 0.5) * depth * 3), 20, 20000, 1000);
        voice.filter.frequency.setTargetAtTime(target, now + (step * intervalSeconds), 0.012);
      }
    }
    this.voices.set(voiceId, voice);
    this.voiceOrder.push(voiceId);
    this.lastPlayedNote = midiNote;
    return voiceId;
  }

  noteOff(noteOrId, immediate = false, scheduledTime = null) {
    let voiceId = String(noteOrId);
    if (!this.voices.has(voiceId) && Number.isFinite(Number(noteOrId))) {
      voiceId = `note:${Math.round(Number(noteOrId))}`;
    }
    const voice = this.voices.get(voiceId);
    if (!voice || (voice.released && !voice.releaseScheduledAt)) return false;
    // Sustain pedal: hold the note until the pedal lifts. Steals, retriggers
    // and panics (immediate) always release.
    if (this.sustainPedal && !immediate) {
      this.sustainedVoiceIds.add(voiceId);
      return true;
    }
    this.sustainedVoiceIds.delete(voiceId);
    const currentAudioTime = this.ctx.currentTime;
    const now = Math.max(currentAudioTime, Number(scheduledTime) || 0);
    const isFutureRelease = now > currentAudioTime + 0.005;
    voice.released = !isFutureRelease;
    voice.releaseScheduledAt = isFutureRelease ? now : null;
    if (voice.cleanupTimer) window.clearTimeout(voice.cleanupTimer);
    const baseRelease = voice.releaseOverride ?? this.patch.ampEnvelope.release;
    const release = immediate ? 0.012 : Math.max(0.01, baseRelease * (1 + this.patch.macros.Space * 0.35));
    // Absolute timeline moment this voice goes silent, so polyphony bookkeeping
    // can retire it without waiting on a realtime cleanup timer.
    voice.releaseEndsAt = now + release;
    if (!immediate) this._playReleaseSamples(voice, now);
    voice.workletNoteIds.forEach((id) => {
      this._sendPhysicalEvent({ type: 'noteOff', id, time: now, immediate });
    });
    voice.coreLayerIds.forEach((layerId) => {
      this._sendCoreEvent(layerId, { type: 'noteOff', id: voice.coreNoteId, time: now });
    });
    const current = Math.max(0.0001, voice.envelope.gain.value || 0.0001);
    voice.envelope.gain.cancelScheduledValues(now);
    voice.envelope.gain.setValueAtTime(current, now);
    voice.envelope.gain.exponentialRampToValueAtTime(0.0001, now + release);
    voice.afmNodes.forEach(node => node.parameters.get('gate').setValueAtTime(0, now));
    voice.layerEnvelopes.forEach(({ node, release: layerRelease }) => {
      const duration = immediate ? 0.012 : Math.max(0.005, layerRelease);
      node.gain.cancelScheduledValues(now);
      node.gain.setValueAtTime(Math.max(0.0001, node.gain.value || 0.0001), now);
      node.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    });
    if (!immediate && voice.filter) {
      const releaseCutoff = clamp(voice.baseCutoff || this.patch.filter.cutoff, 20, 20000, 1000);
      voice.filter.frequency.cancelScheduledValues(now);
      voice.filter.frequency.setValueAtTime(Math.max(20, voice.filter.frequency.value || releaseCutoff), now);
      voice.filter.frequency.exponentialRampToValueAtTime(releaseCutoff, now + release);
    }
    voice.sources.forEach((source) => {
      try { source.stop(now + release + 0.05); } catch (_) { /* source already stopped */ }
    });
    voice.cleanupTimer = window.setTimeout(() => {
      voice.released = true;
      voice.releaseScheduledAt = null;
      voice.afmNodes.forEach(node => this._recycleAfm(node));
      voice.nodes.forEach(disconnectNode);
      // A fast retrigger can reuse the same voice id before this older voice's
      // release timer fires. Never let the old cleanup delete the replacement;
      // doing so loses the only handle capable of stopping the new oscillators.
      if (this.voices.get(voiceId) === voice) {
        this.voices.delete(voiceId);
        this.voiceOrder = this.voiceOrder.filter((entry) => entry !== voiceId);
      }
    }, Math.ceil(((now - this.ctx.currentTime) + release + 0.1) * 1000));
    return true;
  }

  /** MIDI CC64. Lifting the pedal releases every note it was holding. */
  setSustain(down) {
    if ((down === true) !== this.sustainPedal) this._playPedalSamples(down === true);
    this.sustainPedal = down === true;
    if (this.sustainPedal) return;
    const held = [...this.sustainedVoiceIds];
    this.sustainedVoiceIds.clear();
    held.forEach((voiceId) => this.noteOff(voiceId, false));
  }

  /** MIDI CC1, 0..1. Core layers route it to their filter cutoff amount. */
  setModWheel(value) {
    this.modWheel = clamp(value, 0, 1, 0);
    const time = this.ctx.currentTime;
    this.coreLayers.forEach((_, layerId) => {
      this._sendCoreEvent(layerId, { type: 'modWheel', value: this.modWheel, time });
    });
  }

  /** Global bend plus the voice's own (MPE) bend, onto a Web Audio voice's pitch params. */
  _applyVoicePitch(voice, time, smoothing) {
    const semitones = this.pitchBend + (voice.noteBend || 0);
    voice.pitchParams.forEach(({ param, offset }) => {
      param.setTargetAtTime(offset + semitones * 100, time, smoothing);
    });
    voice.playbackRateParams.forEach(({ param, baseRate }) => {
      param.setTargetAtTime(baseRate * Math.pow(2, semitones / 12), time, smoothing);
    });
  }

  /**
   * Per-note expression (MPE, polyphonic aftertouch) for one sounding voice:
   * `pitch` in semitones, `pressure` and `timbre` 0..1. Pitch reaches every
   * engine; pressure and timbre are Core mod-matrix sources.
   */
  setNoteExpression(voiceId, { pitch, pressure, timbre } = {}, scheduledTime = null) {
    const voice = this.voices.get(voiceId);
    if (!voice) return false;
    const time = Math.max(this.ctx.currentTime, Number(scheduledTime) || 0);
    const core = (type, value) => (voice.coreLayerIds || []).forEach((layerId) => {
      this._sendCoreEvent(layerId, { type, id: voice.coreNoteId, value, time });
    });
    if (Number.isFinite(pitch)) {
      voice.noteBend = clamp(pitch, -96, 96, 0);
      this._applyVoicePitch(voice, time, 0.004);
      core('notePitch', voice.noteBend);
    }
    if (Number.isFinite(pressure)) core('notePressure', clamp(pressure, 0, 1, 0));
    if (Number.isFinite(timbre)) core('noteTimbre', clamp(timbre, 0, 1, 0));
    return true;
  }

  /** MIDI channel pressure, 0..1: a Core mod-matrix source. */
  setAftertouch(value) {
    this.aftertouch = clamp(value, 0, 1, 0);
    const time = this.ctx.currentTime;
    this.coreLayers.forEach((_, layerId) => {
      this._sendCoreEvent(layerId, { type: 'aftertouch', value: this.aftertouch, time });
    });
  }

  setPitchBend(normalized) {
    const bendNormalized = clamp(normalized, -1, 1, 0);
    this.coreLayers.forEach((_, layerId) => {
      this._sendCoreEvent(layerId, { type: 'pitchBend', value: bendNormalized, time: this.ctx.currentTime });
    });
    this.pitchBend = clamp(normalized, -1, 1, 0) * this.patch.performance.pitchBendRange;
    const now = this.ctx.currentTime;
    this.voices.forEach((voice) => this._applyVoicePitch(voice, now, 0.008));
    this._sendPhysicalEvent({
      type: 'pitchBend',
      semitones: this.pitchBend,
      time: now
    });
  }

  setTempo(bpm) {
    this.tempo = clamp(bpm, 30, 300, 120);
    // Tempo-synced Core LFOs read the tempo from their parameter block.
    this.coreLayers.forEach((entry) => {
      if (!entry.params || entry.params[CORE_PARAMS.TEMPO] === this.tempo) return;
      entry.params[CORE_PARAMS.TEMPO] = this.tempo;
      entry.node?.port.postMessage({ type: 'params', params: entry.params });
    });
  }

  /**
   * Drop voices whose scheduled release has already ended by `upTo` from the
   * polyphony bookkeeping. Bookkeeping only: the nodes stay connected so any
   * audio still scheduled between the audio clock and `upTo` renders normally,
   * and the voice's own cleanup timer (or destroy()) disconnects them.
   */
  _retireFinishedVoices(upTo) {
    if (!this.voiceOrder.length) return;
    this.voiceOrder = this.voiceOrder.filter((voiceId) => {
      const voice = this.voices.get(voiceId);
      if (!voice) return false;
      if (!Number.isFinite(voice.releaseEndsAt) || voice.releaseEndsAt > upTo) return true;
      this.voices.delete(voiceId);
      return false;
    });
  }

  /**
   * Remove only notes that Web Audio has queued but has not begun rendering.
   * Live piano-roll edits use this instead of panic/allNotesOff so sustained
   * notes continue naturally while the edited lookahead window is rebuilt.
   */
  cancelScheduledVoices(afterTime = this.ctx.currentTime) {
    const cutoff = Math.max(this.ctx.currentTime, Number(afterTime) || 0);
    const now = this.ctx.currentTime;
    let cancelled = 0;
    this.coreLayers.forEach((entry) => this._cancelCoreAfter(entry, cutoff));
    [...this.voices.entries()].forEach(([voiceId, voice]) => {
      if (!Number.isFinite(voice?.startedAt) || voice.startedAt <= cutoff) return;
      cancelled += 1;
      if (voice.cleanupTimer) window.clearTimeout(voice.cleanupTimer);
      voice.workletNoteIds.forEach((id) => {
        this._sendPhysicalEvent({ type: 'noteOff', id, time: now, immediate: true });
      });
      voice.sources.forEach((source) => {
        try { source.stop(now); } catch (_) { /* already stopped */ }
      });
      voice.afmNodes.forEach(node => this._recycleAfm(node));
      voice.nodes.forEach(disconnectNode);
      this.voices.delete(voiceId);
      this.voiceOrder = this.voiceOrder.filter((entry) => entry !== voiceId);
    });
    return cancelled;
  }

  allNotesOff(immediate = false) {
    // A panic must not leave notes parked on the sustain pedal.
    const wasSustained = this.sustainPedal;
    this.sustainPedal = false;
    this.sustainedVoiceIds.clear();
    [...this.voices.keys()].forEach((voiceId) => this.noteOff(voiceId, immediate));
    // The pedal is still physically down; later notes keep honouring it.
    this.sustainPedal = wasSustained;
    this.coreLayers.forEach((_, layerId) => {
      this._sendCoreEvent(layerId, { type: 'allNotesOff', value: immediate ? 1 : 0, time: this.ctx.currentTime });
    });
    this._sendPhysicalEvent({
      type: 'allNotesOff',
      immediate,
      time: this.ctx.currentTime
    });
    if (immediate) this.lastPlayedNote = null;
  }

  getAnalyser() {
    return this.analyser;
  }

  destroy() {
    this.destroyed = true;
    this.afmPool.forEach(({ node }) => { node.port.postMessage('dispose'); node.disconnect(); });
    disconnectNode(this.afmIdleGain);
    this.allNotesOff(true);
    try { this.physicalWorklet?.port.postMessage({ type: 'destroy' }); } catch (_) { /* noop */ }
    this.coreLayers.forEach((entry) => this._disposeCoreLayer(entry));
    this.coreLayers.clear();
    this.hostedLayers.clear();
    this.hostedPinned.clear();
    try { this.chorusLfo.stop(); } catch (_) { /* noop */ }
    [
      this.input, this.shaper, this.dryGain, this.chorusDelay, this.chorusWet,
      this.chorusLfo, this.chorusDepth, this.delay, this.delayFeedback,
      this.delayWet, this.convolver, this.reverbWet, this.compressor,
      this.master, this.safetyLimiter, this.analyser, this.physicalWorklet
    ].forEach(disconnectNode);
  }
}

export default NativeInstrumentEngine;
